import React, { useState, useMemo, useEffect } from 'react';
import { Student, Teacher, SchoolSettings, UserRole, AttendanceRecord, HomeroomJournalNote, StudentAcademicProgress, StudentExtracurricular, AgendaEntry, ScheduleEntry } from '../types.ts';
import * as XLSX from 'xlsx';
import WaliRombelView from './WaliRombelView.tsx';
import ClassAttendanceManager from '../src/components/ClassAttendanceManager.tsx';
import HomeroomJournalManager from '../src/components/HomeroomJournalManager.tsx';
import StudentAcademicProgressManager from '../src/components/StudentAcademicProgressManager.tsx';
import { ReportCardNotesManager } from '../src/components/ReportCardNotesManager.tsx';
import { ExtracurricularManager } from '../src/components/ExtracurricularManager.tsx';
import { PramukaAdministrationManager } from '../src/components/PramukaAdministrationManager.tsx';
import { MonthlyClassReportManager } from '../src/components/MonthlyClassReportManager.tsx';
import { getThemeById } from '../src/themeConfig.ts';

import { 
  UserCheck, 
  Users, 
  Search, 
  Printer, 
  Building2, 
  ShieldCheck, 
  Award, 
  Edit3, 
  Check, 
  X, 
  FileText, 
  ChevronRight, 
  Sparkles,
  Info,
  Calendar,
  Filter,
  User,
  GraduationCap,
  Lock,
  Eye,
  Plus,
  Trash2,
  Phone,
  MapPin,
  CreditCard,
  Camera,
  Briefcase,
  Heart,
  PhoneCall,
  Download,
  BookOpen,
  FileBadge,
  UserPlus,
  CalendarDays,
  FileSpreadsheet,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Compass,
  HeartHandshake,
  TrendingUp,
  Layers
} from 'lucide-react';

interface PerwalianViewProps {
  waliRombel: any[];
  students: Student[];
  classes: string[];
  activeUser: Teacher;
  schoolSettings: SchoolSettings;
  onSaveWali: (item: any, method: 'POST' | 'PUT' | 'DELETE') => Promise<any>;
  onUpdateTeacher?: (teacher: Teacher) => void | Promise<any>;
  onUpdateStudents?: (students: Student[]) => void | Promise<any>;
  attendanceRecords?: AttendanceRecord[];
  onUpdateAttendance?: (records: AttendanceRecord[]) => void | Promise<any>;
  userRole?: UserRole;
  homeroomNotes?: HomeroomJournalNote[];
  onSaveHomeroomNote?: (note: HomeroomJournalNote, method: 'POST' | 'PUT' | 'DELETE') => void | Promise<any>;
  academicProgress?: StudentAcademicProgress[];
  onSaveAcademicProgress?: (progress: StudentAcademicProgress, method: 'POST' | 'PUT' | 'DELETE') => Promise<any>;
  extracurriculars?: StudentExtracurricular[];
  onSaveExtracurricular?: (item: StudentExtracurricular, method: 'POST' | 'PUT' | 'DELETE') => Promise<any>;
  onUpdateSettings?: (settings: SchoolSettings) => Promise<any>;
  appTheme?: string;
  agendas?: AgendaEntry[];
  agendaAttendanceRecords?: AttendanceRecord[];
  schedules?: ScheduleEntry[];
}

interface StudentClassRole {
  studentId: string;
  role: string;
  note: string;
}

const PerwalianView: React.FC<PerwalianViewProps> = ({
  waliRombel = [],
  students = [],
  classes = [],
  activeUser,
  schoolSettings,
  onSaveWali,
  onUpdateTeacher,
  onUpdateStudents,
  attendanceRecords = [],
  onUpdateAttendance,
  userRole,
  homeroomNotes = [],
  onSaveHomeroomNote,
  academicProgress = [],
  onSaveAcademicProgress,
  extracurriculars = [],
  onSaveExtracurricular,
  onUpdateSettings,
  appTheme = 'wzc-themes',
  agendas = [],
  agendaAttendanceRecords = [],
  schedules = [],
}) => {
  // Active theme configuration
  const currentGlobalTheme = useMemo(() => getThemeById(appTheme), [appTheme]);

  // Main view tab: 'wali_kelas' | 'wali_rombel' | 'administrasi_pasukan'
  const [mainTab, setMainTab] = useState<'wali_kelas' | 'wali_rombel' | 'administrasi_pasukan'>('wali_kelas');

  // Sub-tab under Wali Kelas: 'database' | 'kehadiran' | 'laporan-bulanan' | 'catatan' | 'perkembangan' | 'catatan-rapor' | 'extrakurikuler'
  const [wkSubTab, setWkSubTab] = useState<'database' | 'kehadiran' | 'laporan-bulanan' | 'catatan' | 'perkembangan' | 'catatan-rapor' | 'extrakurikuler'>('database');


  // Check if admin or if homeroom class is locked for regular teacher
  const isAdmin = userRole === UserRole.Admin || activeUser?.username === 'admin';
  const isClassLocked = !isAdmin && !!activeUser?.homeroomClass;

  // Selected Homeroom Class state
  const [selectedHomeroomClass, setSelectedHomeroomClass] = useState<string>(() => {
    return (
      activeUser?.homeroomClass ||
      (activeUser?.assignedClasses && activeUser.assignedClasses[0]) ||
      classes[0] ||
      ''
    );
  });

  // Keep state synced if activeUser updates
  useEffect(() => {
    if (activeUser?.homeroomClass) {
      setSelectedHomeroomClass(activeUser.homeroomClass);
    } else if (!selectedHomeroomClass && classes.length > 0) {
      setSelectedHomeroomClass(classes[0]);
    }
  }, [activeUser, classes]);

  // Search & Filters for Wali Kelas
  const [wkSearchQuery, setWkSearchQuery] = useState('');
  const [wkGenderFilter, setWkGenderFilter] = useState<'SEMUA' | 'Laki-laki' | 'Perempuan'>('SEMUA');
  const [wkStatusFilter, setWkStatusFilter] = useState<'SEMUA' | 'Aktif' | 'Tidak Aktif'>('SEMUA');
  const [wkKipFilter, setWkKipFilter] = useState<'SEMUA' | 'Penerima KIP/PIP' | 'Bukan Penerima'>('SEMUA');
  const [wkIncompleteFilter, setWkIncompleteFilter] = useState<'SEMUA' | 'BELUM_LENGKAP' | 'NISN' | 'NIK' | 'KK' | 'ORTU' | 'HP' | 'ALAMAT' | 'IJAZAH' | 'FOTO' | 'STUDI'>('SEMUA');

  // Detail Modal State for Database Utama Wali Kelas
  const [selectedStudentDetail, setSelectedStudentDetail] = useState<Student | null>(null);
  const [isEditingDetail, setIsEditingDetail] = useState(false);
  const [detailTab, setDetailTab] = useState<'identitas' | 'orangtua' | 'pendukung'>('identitas');
  const [detailFormData, setDetailFormData] = useState<Partial<Student>>({});
  const [isAddingStudent, setIsAddingStudent] = useState(false);

  // Class Roles / Notes per student
  const [classRoles, setClassRoles] = useState<Record<string, StudentClassRole>>(() => {
    try {
      const saved = localStorage.getItem(`smpn1_class_roles_${activeUser?.id}`);
      return saved ? JSON.parse(saved) : {};
    } catch (e) {
      return {};
    }
  });

  useEffect(() => {
    // Populate class roles directly from students array coming from Firebase
    const mapFromStudents: Record<string, StudentClassRole> = {};
    students.forEach((s) => {
      if (s.classRole || s.notes || s.classRoleNote) {
        mapFromStudents[s.id] = {
          studentId: s.id,
          role: s.classRole || '',
          note: s.notes || s.classRoleNote || '',
        };
      }
    });
    setClassRoles((prev) => ({ ...mapFromStudents, ...prev }));
  }, [students]);

  // Editing student role inline modal/state
  const [editingRoleId, setEditingRoleId] = useState<string | null>(null);
  const [inputRole, setInputRole] = useState('');
  const [inputNote, setInputNote] = useState('');

  // General Wali Kelas Notes
  const [classNotes, setClassNotes] = useState<string>(() => {
    try {
      return localStorage.getItem(`smpn1_wk_notes_${activeUser?.id}_${selectedHomeroomClass}`) || '';
    } catch {
      return '';
    }
  });

  const [isEditingNotes, setIsEditingNotes] = useState(false);
  const [notesInput, setNotesInput] = useState(classNotes);

  useEffect(() => {
    const noteId = `wk_general_${activeUser?.id}_${selectedHomeroomClass}`;
    const fbNote = homeroomNotes.find((n) => n.id === noteId);
    if (fbNote) {
      setClassNotes(fbNote.description || '');
      setNotesInput(fbNote.description || '');
    } else {
      try {
        const saved = localStorage.getItem(`smpn1_wk_notes_${activeUser?.id}_${selectedHomeroomClass}`) || '';
        setClassNotes(saved);
        setNotesInput(saved);
      } catch (e) {
        console.error(e);
      }
    }
  }, [selectedHomeroomClass, activeUser?.id, homeroomNotes]);

  const handleSaveNotes = async () => {
    setClassNotes(notesInput);
    setIsEditingNotes(false);
    const noteId = `wk_general_${activeUser?.id}_${selectedHomeroomClass}`;
    const noteObj: HomeroomJournalNote = {
      id: noteId,
      date: new Date().toISOString().split('T')[0],
      teacherId: activeUser?.id || '',
      teacherName: activeUser?.name || 'Wali Kelas',
      studentId: 'ALL',
      studentName: `Semua Siswa Kelas ${selectedHomeroomClass}`,
      homeroomClass: selectedHomeroomClass,
      category: 'Akademik',
      status: 'Selesai',
      description: notesInput,
      actionTaken: '',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    if (onSaveHomeroomNote) {
      await onSaveHomeroomNote(noteObj, 'PUT');
    }
    try {
      localStorage.setItem(`smpn1_wk_notes_${activeUser?.id}_${selectedHomeroomClass}`, notesInput);
    } catch (e) {
      console.error(e);
    }
  };

  // Save selected homeroom class to teacher profile if onUpdateTeacher is provided
  const handleSelectHomeroomClass = async (newClass: string) => {
    setSelectedHomeroomClass(newClass);
    if (onUpdateTeacher && activeUser && !isClassLocked) {
      const updatedTeacher = { ...activeUser, homeroomClass: newClass };
      await onUpdateTeacher(updatedTeacher);
    }
  };

  // Filter students belonging to the selected Homeroom Class
  const homeroomStudents = useMemo(() => {
    if (!selectedHomeroomClass) return [];
    return students.filter((s) => s.class === selectedHomeroomClass);
  }, [students, selectedHomeroomClass]);

  // Filtered list for display
  const filteredWkStudents = useMemo(() => {
    return homeroomStudents.filter((s) => {
      const q = wkSearchQuery.toLowerCase().trim();
      const matchesSearch =
        !q ||
        s.name.toLowerCase().includes(q) ||
        (s.nickname && s.nickname.toLowerCase().includes(q)) ||
        (s.nis && s.nis.toLowerCase().includes(q)) ||
        (s.nisn && s.nisn.toLowerCase().includes(q)) ||
        (s.nik && s.nik.toLowerCase().includes(q)) ||
        (s.familyCardNo && s.familyCardNo.toLowerCase().includes(q)) ||
        (s.elementaryCertNo && s.elementaryCertNo.toLowerCase().includes(q)) ||
        (s.fatherName && s.fatherName.toLowerCase().includes(q)) ||
        (s.motherName && s.motherName.toLowerCase().includes(q)) ||
        (s.postalCode && s.postalCode.toLowerCase().includes(q)) ||
        (s.parentPostalCode && s.parentPostalCode.toLowerCase().includes(q)) ||
        (s.rencanaStudiLanjutan && s.rencanaStudiLanjutan.toLowerCase().includes(q)) ||
        (s.rencanaStudiDetail && s.rencanaStudiDetail.toLowerCase().includes(q));

      const matchesGender = wkGenderFilter === 'SEMUA' || s.gender === wkGenderFilter;
      const matchesStatus = wkStatusFilter === 'SEMUA' || s.status === wkStatusFilter;

      let matchesKip = true;
      if (wkKipFilter === 'Penerima KIP/PIP') {
        matchesKip = !!(s.kipPipStatus && s.kipPipStatus !== 'Bukan Penerima');
      } else if (wkKipFilter === 'Bukan Penerima') {
        matchesKip = !s.kipPipStatus || s.kipPipStatus === 'Bukan Penerima';
      }

      let matchesIncomplete = true;
      if (wkIncompleteFilter === 'BELUM_LENGKAP') {
        matchesIncomplete = !s.nisn || !s.nik || !s.familyCardNo || !s.elementaryCertNo || (!s.fatherName && !s.motherName && !s.guardianName) || (!s.phone && !s.parentPhone) || !s.address || !s.photoUrl;
      } else if (wkIncompleteFilter === 'NISN') {
        matchesIncomplete = !s.nisn || s.nisn.trim() === '';
      } else if (wkIncompleteFilter === 'NIK') {
        matchesIncomplete = !s.nik || s.nik.trim() === '';
      } else if (wkIncompleteFilter === 'KK') {
        matchesIncomplete = !s.familyCardNo || s.familyCardNo.trim() === '';
      } else if (wkIncompleteFilter === 'ORTU') {
        matchesIncomplete = !s.fatherName && !s.motherName && !s.guardianName;
      } else if (wkIncompleteFilter === 'HP') {
        matchesIncomplete = !s.phone && !s.parentPhone;
      } else if (wkIncompleteFilter === 'ALAMAT') {
        matchesIncomplete = !s.address || s.address.trim() === '';
      } else if (wkIncompleteFilter === 'IJAZAH') {
        matchesIncomplete = !s.elementaryCertNo || s.elementaryCertNo.trim() === '';
      } else if (wkIncompleteFilter === 'FOTO') {
        matchesIncomplete = !s.photoUrl || s.photoUrl.trim() === '';
      } else if (wkIncompleteFilter === 'STUDI') {
        matchesIncomplete = !s.rencanaStudiLanjutan || s.rencanaStudiLanjutan.trim() === '' || !s.rencanaStudiDetail || s.rencanaStudiDetail.trim() === '';
      }

      return matchesSearch && matchesGender && matchesStatus && matchesKip && matchesIncomplete;
    });
  }, [homeroomStudents, wkSearchQuery, wkGenderFilter, wkStatusFilter, wkKipFilter, wkIncompleteFilter]);

  // Table Column Sorting State
  type WkSortField = 'name' | 'nis' | 'gender' | 'phone' | 'parent' | 'parentPhone' | 'role';
  type WkSortOrder = 'asc' | 'desc';

  const [wkSortField, setWkSortField] = useState<WkSortField>('name');
  const [wkSortOrder, setWkSortOrder] = useState<WkSortOrder>('asc');

  const handleWkSort = (field: WkSortField) => {
    if (wkSortField === field) {
      setWkSortOrder(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setWkSortField(field);
      setWkSortOrder('asc');
    }
  };

  const sortedFilteredWkStudents = useMemo(() => {
    return [...filteredWkStudents].sort((a, b) => {
      let valA = '';
      let valB = '';

      if (wkSortField === 'name') {
        valA = (a.name || '').toLowerCase();
        valB = (b.name || '').toLowerCase();
      } else if (wkSortField === 'nis') {
        valA = (a.nis || a.nisn || a.nik || '').toLowerCase();
        valB = (b.nis || b.nisn || b.nik || '').toLowerCase();
      } else if (wkSortField === 'gender') {
        valA = (a.gender || '').toLowerCase();
        valB = (b.gender || '').toLowerCase();
      } else if (wkSortField === 'phone') {
        valA = (a.phone || '').toLowerCase();
        valB = (b.phone || '').toLowerCase();
      } else if (wkSortField === 'parent') {
        valA = (a.fatherName || a.motherName || a.guardianName || '').toLowerCase();
        valB = (b.fatherName || b.motherName || b.guardianName || '').toLowerCase();
      } else if (wkSortField === 'parentPhone') {
        valA = (a.parentPhone || '').toLowerCase();
        valB = (b.parentPhone || '').toLowerCase();
      } else if (wkSortField === 'role') {
        const roleA = a.classRole || classRoles[a.id]?.role || '';
        const roleB = b.classRole || classRoles[b.id]?.role || '';
        valA = roleA.toLowerCase();
        valB = roleB.toLowerCase();
      }

      if (valA < valB) return wkSortOrder === 'asc' ? -1 : 1;
      if (valA > valB) return wkSortOrder === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filteredWkStudents, wkSortField, wkSortOrder, classRoles]);

  const renderWkSortHeader = (label: string, field: WkSortField, align: 'left' | 'center' | 'right' = 'left') => {
    const isActive = wkSortField === field;
    return (
      <button
        type="button"
        onClick={() => handleWkSort(field)}
        className={`group inline-flex items-center gap-0.5 font-bold text-[10px] xl:text-[11px] uppercase tracking-tight hover:text-emerald-600 dark:hover:text-emerald-400 transition-colors leading-tight ${
          isActive ? 'text-emerald-600 dark:text-emerald-400' : 'text-slate-600 dark:text-slate-400'
        } ${align === 'center' ? 'mx-auto justify-center text-center' : align === 'right' ? 'ml-auto justify-end text-right' : 'justify-start text-left'}`}
      >
        <span className="break-words">{label}</span>
        {isActive ? (
          wkSortOrder === 'asc' ? <ArrowUp size={10} className="shrink-0 ml-0.5" /> : <ArrowDown size={10} className="shrink-0 ml-0.5" />
        ) : (
          <ArrowUpDown size={9} className="opacity-40 group-hover:opacity-100 shrink-0 ml-0.5" />
        )}
      </button>
    );
  };

  // Stats for Wali Kelas
  const wkStats = useMemo(() => {
    const total = homeroomStudents.length;
    const laki = homeroomStudents.filter((s) => s.gender === 'Laki-laki').length;
    const perempuan = homeroomStudents.filter((s) => s.gender === 'Perempuan').length;
    const aktif = homeroomStudents.filter((s) => s.status === 'Aktif').length;
    const nonAktif = total - aktif;
    const kipPip = homeroomStudents.filter((s) => s.kipPipStatus && s.kipPipStatus !== 'Bukan Penerima').length;
    return { total, laki, perempuan, aktif, nonAktif, kipPip };
  }, [homeroomStudents]);

  // Handle Edit Role/Note
  const handleStartEditRole = (student: Student) => {
    setEditingRoleId(student.id);
    const existing = classRoles[student.id];
    setInputRole(student.classRole || existing?.role || '');
    setInputNote(student.notes || student.classRoleNote || existing?.note || '');
  };

  const handleSaveRole = (studentId: string) => {
    const roleVal = inputRole.trim();
    const noteVal = inputNote.trim();

    // Update main students list if handler provided
    if (onUpdateStudents) {
      const updatedStudents = students.map((s) => {
        if (s.id === studentId) {
          return {
            ...s,
            classRole: roleVal || undefined,
            notes: noteVal || undefined,
            classRoleNote: noteVal || undefined,
          };
        }
        return s;
      });
      onUpdateStudents(updatedStudents);
    }

    // Also update local classRoles state
    setClassRoles((prev) => ({
      ...prev,
      [studentId]: {
        studentId,
        role: roleVal,
        note: noteVal,
      },
    }));
    setEditingRoleId(null);
  };

  // Open Full Detail Modal
  const handleOpenStudentDetail = (student: Student, editMode: boolean = false) => {
    setSelectedStudentDetail(student);
    setIsEditingDetail(editMode);
    setIsAddingStudent(false);
    setDetailTab('identitas');
    setDetailFormData({ ...student });
  };

  // Open Add Student Modal
  const handleOpenAddStudent = () => {
    const blankStudent: Partial<Student> = {
      id: `std_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`,
      name: '',
      nis: '',
      nisn: '',
      class: selectedHomeroomClass || classes[0] || '7A',
      gender: 'Laki-laki',
      status: 'Aktif',
      nickname: '',
      pob: '',
      dob: '',
      nik: '',
      religion: 'Islam',
      address: '',
      postalCode: '',
      phone: '',
      fatherName: '',
      motherName: '',
      guardianName: '',
      parentPhone: '',
      parentJob: '',
      parentAddress: '',
      parentPostalCode: '',
      emergencyContact: '',
      photoUrl: '',
      familyCardNo: '',
      elementaryCertNo: '',
      supportingDocUrl: '',
      specialNotes: '',
      kipPipStatus: 'Bukan Penerima',
    };
    setDetailFormData(blankStudent);
    setSelectedStudentDetail(blankStudent as Student);
    setIsAddingStudent(true);
    setIsEditingDetail(true);
    setDetailTab('identitas');
  };

  // Save Full Detail Student
  const handleSaveStudentDetail = () => {
    if (!detailFormData.name || !detailFormData.name.trim()) {
      alert('Nama lengkap siswa wajib diisi.');
      return;
    }

    let updatedStudents: Student[] = [];
    if (isAddingStudent) {
      const newStudent: Student = {
        id: detailFormData.id || `std_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`,
        name: detailFormData.name.trim(),
        nis: detailFormData.nis?.trim() || '',
        nisn: detailFormData.nisn?.trim() || '',
        class: selectedHomeroomClass || detailFormData.class || classes[0] || '7A',
        gender: detailFormData.gender || 'Laki-laki',
        status: detailFormData.status || 'Aktif',
        ...detailFormData,
      } as Student;
      updatedStudents = [newStudent, ...students];
    } else if (selectedStudentDetail) {
      updatedStudents = students.map((s) =>
        s.id === selectedStudentDetail.id ? ({ ...s, ...detailFormData } as Student) : s
      );
    }

    if (onUpdateStudents) {
      onUpdateStudents(updatedStudents);
    }

    setSelectedStudentDetail(null);
    setIsAddingStudent(false);
    setIsEditingDetail(false);
  };

  // Delete Student from class
  const handleDeleteStudent = (studentId: string) => {
    if (window.confirm('Apakah Anda yakin ingin menghapus data siswa ini dari database?')) {
      const updated = students.filter((s) => s.id !== studentId);
      if (onUpdateStudents) {
        onUpdateStudents(updated);
      }
      if (selectedStudentDetail?.id === studentId) {
        setSelectedStudentDetail(null);
      }
    }
  };

  // Upload Photo Handler
  const handlePhotoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 3 * 1024 * 1024) {
      alert('Ukuran foto maksimal 3MB.');
      return;
    }
    const reader = new FileReader();
    reader.onloadend = () => {
      setDetailFormData((prev) => ({ ...prev, photoUrl: reader.result as string }));
    };
    reader.readAsDataURL(file);
  };

  // Upload Document Handler
  const handleDocUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      alert('Ukuran dokumen maksimal 5MB.');
      return;
    }
    const reader = new FileReader();
    reader.onloadend = () => {
      setDetailFormData((prev) => ({ ...prev, supportingDocUrl: reader.result as string }));
    };
    reader.readAsDataURL(file);
  };

  // Export Wali Kelas to Excel
  const handleExportWkExcel = () => {
    const schoolTitle = schoolSettings.schoolName || 'SMP NEGERI 1';
    
    // Header Data
    const headerData = [
      [schoolSettings.governmentName || 'PEMERINTAH KABUPATEN CIAMIS'],
      [schoolSettings.departmentName || 'DINAS PENDIDIKAN'],
      [schoolTitle.toUpperCase()],
      [schoolSettings.address || ''],
      [],
      [`DATABASE ADMINISTRASI WALAS - KELAS ${selectedHomeroomClass}`],
      [`Wali Kelas: ${activeUser.name} (NIP. ${activeUser.nip || '-'})`],
      [`Tahun Ajaran: ${schoolSettings.academicYear || '2025/2026'} - Semester: ${schoolSettings.semester || 'Ganjil'}`],
      [],
      [
        'No', 
        'Nama Lengkap', 
        'Nama Panggilan', 
        'NIS', 
        'NISN', 
        'NIK', 
        'No KK', 
        'Jenis Kelamin', 
        'Status', 
        'No HP Siswa', 
        'Alamat Lengkap', 
        'Kode Pos Siswa',
        'Nama Ayah', 
        'Nama Ibu', 
        'Nama Wali', 
        'No HP Ortu/Wali', 
        'Alamat Ortu',
        'Kode Pos Ortu',
        'Status KIP/PIP', 
        'Rencana Studi Lanjutan', 
        'Detail Rencana Studi', 
        'Jabatan Kelas', 
        'Catatan Khusus'
      ]
    ];

    // Student Data
    const studentData = sortedFilteredWkStudents.map((s, idx) => {
      const roleData = classRoles[s.id];
      const activeRole = s.classRole || roleData?.role || '';
      const activeNote = s.notes || s.classRoleNote || roleData?.note || '';
      return [
        idx + 1,
        s.name,
        s.nickname || '',
        s.nis || '',
        s.nisn || '',
        s.nik || '',
        s.familyCardNo || '',
        s.gender || '',
        s.status || 'Aktif',
        s.phone || '',
        s.address || '',
        s.postalCode || '',
        s.fatherName || '',
        s.motherName || '',
        s.guardianName || '',
        s.parentPhone || '',
        s.parentAddress || s.address || '',
        s.parentPostalCode || s.postalCode || '',
        s.kipPipStatus || 'Bukan Penerima',
        s.rencanaStudiLanjutan || '',
        s.rencanaStudiDetail || '',
        activeRole,
        activeNote
      ];
    });

    const ws = XLSX.utils.aoa_to_sheet([...headerData, ...studentData]);
    
    // Set column widths
    ws['!cols'] = [
      { wch: 5 },   // No
      { wch: 25 },  // Nama Lengkap
      { wch: 15 },  // Nama Panggilan
      { wch: 12 },  // NIS
      { wch: 15 },  // NISN
      { wch: 18 },  // NIK
      { wch: 18 },  // KK
      { wch: 15 },  // Gender
      { wch: 10 },  // Status
      { wch: 15 },  // HP Siswa
      { wch: 35 },  // Alamat Siswa
      { wch: 15 },  // Kode Pos Siswa
      { wch: 20 },  // Ayah
      { wch: 20 },  // Ibu
      { wch: 20 },  // Wali
      { wch: 15 },  // HP Ortu
      { wch: 35 },  // Alamat Ortu
      { wch: 15 },  // Kode Pos Ortu
      { wch: 18 },  // KIP
      { wch: 22 },  // Rencana Studi Lanjutan
      { wch: 25 },  // Detail Rencana
      { wch: 15 },  // Jabatan
      { wch: 25 }   // Catatan
    ];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Database_Siswa");
    XLSX.writeFile(wb, `Administrasi_Walas_Kelas_${selectedHomeroomClass}_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  // Print Roster
  const handlePrintWkRoster = () => {
    const printWin = window.open('', '_blank');
    if (!printWin) return;

    const schoolTitle = schoolSettings.schoolName || 'SMP NEGERI 1';
    const content = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Daftar Siswa Wali Kelas ${selectedHomeroomClass} - ${schoolTitle}</title>
          <style>
            @page {
              size: A4;
              margin: 15mm;
            }
            body {
              font-family: 'Times New Roman', Times, serif;
              color: #000;
              line-height: 1.3;
              margin: 0;
              padding: 0;
            }
            .header {
              text-align: center;
              border-bottom: 2px solid #000;
              padding-bottom: 10px;
              margin-bottom: 20px;
            }
            .header h1 {
              font-size: 16pt;
              margin: 0;
              text-transform: uppercase;
            }
            .header h2 {
              font-size: 14pt;
              margin: 5px 0 0 0;
              text-transform: uppercase;
            }
            .header p {
              font-size: 10pt;
              margin: 3px 0 0 0;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              margin-top: 15px;
              font-size: 10pt;
            }
            th, td {
              border: 1px solid #000;
              padding: 6px 8px;
              text-align: left;
            }
            th {
              background-color: #f2f2f2;
              text-align: center;
              font-weight: bold;
            }
            .text-center {
              text-align: center;
            }
            .footer-sig {
              margin-top: 40px;
              width: 100%;
            }
            .footer-sig td {
              border: none;
              text-align: center;
              vertical-align: top;
            }
            .sig-space {
              height: 60px;
            }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>${schoolTitle.toUpperCase()}</h1>
            <h2>DAFTAR SISWA KELAS ${selectedHomeroomClass}</h2>
            <p>Tahun Ajaran ${schoolSettings.academicYear || '2025/2026'} - Semester ${schoolSettings.semester || 'Ganjil'}</p>
          </div>

          <p><strong>Wali Kelas:</strong> ${activeUser.name} (NIP. ${activeUser.nip || '-'})</p>

          <table>
            <thead>
              <tr>
                <th width="5%">NO</th>
                <th width="15%">NIS / NISN</th>
                <th width="28%">NAMA SISWA</th>
                <th width="8%">L/P</th>
                <th width="12%">NO HP</th>
                <th width="12%">STATUS KIP</th>
                <th width="20%">JABATAN / CATATAN</th>
              </tr>
            </thead>
            <tbody>
              ${
                homeroomStudents.length === 0
                  ? '<tr><td colSpan="7" class="text-center">Belum ada data siswa</td></tr>'
                  : homeroomStudents
                      .map((s, idx) => {
                        const roleData = classRoles[s.id];
                        const activeRole = s.classRole || roleData?.role || '';
                        const activeNote = s.notes || s.classRoleNote || roleData?.note || '';
                        return `
                        <tr>
                          <td class="text-center">${idx + 1}</td>
                          <td class="text-center">${s.nis || '-'}<br/><small>${s.nisn || ''}</small></td>
                          <td><strong>${s.name}</strong>${s.nickname ? ` (${s.nickname})` : ''}</td>
                          <td class="text-center">${s.gender === 'Laki-laki' ? 'L' : 'P'}</td>
                          <td class="text-center">${s.phone || s.parentPhone || '-'}</td>
                          <td class="text-center">${s.kipPipStatus || '-'}</td>
                          <td>${activeRole ? activeRole : '-'} ${activeNote ? `(${activeNote})` : ''}</td>
                        </tr>
                      `;
                      })
                      .join('')
              }
            </tbody>
          </table>

          <table class="footer-sig">
            <tr>
              <td width="50%">
                Mengetahui,<br/>
                Kepala Sekolah
                <div class="sig-space"></div>
                <strong>${schoolSettings.principalName || '................................'}</strong><br/>
                NIP. ${schoolSettings.principalNip || '................................'}
              </td>
              <td width="50%">
                ${schoolSettings.address ? schoolSettings.address.split(',')[0] : 'Sumedang'}, ${new Date().toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' })}<br/>
                Wali Kelas ${selectedHomeroomClass}
                <div class="sig-space"></div>
                <strong>${activeUser.name}</strong><br/>
                NIP. ${activeUser.nip || '................................'}
              </td>
            </tr>
          </table>

          <script>
            window.onload = function() {
              window.print();
            }
          </script>
        </body>
      </html>
    `;

    printWin.document.write(content);
    printWin.document.close();
  };

  return (
    <div className="space-y-6 pb-12">
      {/* Header Banner */}
      <div className={`relative overflow-hidden rounded-3xl bg-gradient-to-r ${currentGlobalTheme.cardGradients[0] || currentGlobalTheme.accentGradient} p-6 md:p-8 text-white shadow-xl transition-all duration-300`}>
        <div className="absolute right-0 top-0 -mr-12 -mt-12 h-64 w-64 rounded-full bg-white/10 blur-3xl" />
        <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div className="space-y-2">
            <div className="inline-flex items-center gap-2 rounded-full bg-white/15 px-3.5 py-1 text-xs font-semibold backdrop-blur-md border border-white/20">
              <Sparkles size={14} className="text-amber-300" />
              <span>Database Utama Wali Kelas</span>
              <span className="opacity-40">•</span>
              <span className="text-[10px] font-extrabold uppercase tracking-wider">{currentGlobalTheme.shortName}</span>
            </div>
            <h1 className="text-2xl md:text-3xl font-black tracking-tight">
              Manajemen Administrasi Walas & Data Peserta Didik
            </h1>
            <p className="text-sm text-white/90 max-w-2xl">
              Pusat data induk wali kelas mencakup Identitas lengkap, Orang Tua/Wali, serta Dokumen Pendukung siswa perwalian Anda.
            </p>
          </div>

          {/* Navigation Tabs Header */}
          <div className="flex flex-wrap items-center gap-2 bg-black/25 p-1.5 rounded-2xl border border-white/15 backdrop-blur-md">
            <button
              onClick={() => setMainTab('wali_kelas')}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold transition-all shadow-sm ${
                mainTab === 'wali_kelas'
                  ? 'bg-white text-slate-900 shadow-lg scale-102 font-black'
                  : 'bg-black/20 text-white hover:bg-black/30'
              }`}
            >
              <UserCheck size={18} />
              <span>Database Wali Kelas</span>
              <span className={`ml-1 px-2 py-0.5 rounded-full text-xs font-extrabold ${mainTab === 'wali_kelas' ? currentGlobalTheme.badgeBg : 'bg-white/20 text-white'}`}>
                {selectedHomeroomClass || 'Utama'}
              </span>
            </button>

            <button
              onClick={() => setMainTab('wali_rombel')}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold transition-all shadow-sm ${
                mainTab === 'wali_rombel'
                  ? 'bg-white text-slate-900 shadow-lg scale-102 font-black'
                  : 'bg-black/20 text-white hover:bg-black/30'
              }`}
            >
              <Users size={18} />
              <span>Halaman Wali Rombel</span>
              <span className="ml-1 px-2 py-0.5 rounded-full text-xs bg-white/20 text-white font-extrabold">
                Kelompok
              </span>
            </button>

            <button
              onClick={() => setMainTab('administrasi_pasukan')}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold transition-all shadow-sm ${
                mainTab === 'administrasi_pasukan'
                  ? 'bg-white text-slate-900 shadow-lg scale-102 font-black'
                  : 'bg-black/20 text-white hover:bg-black/30'
              }`}
            >
              <Compass size={18} />
              <span>Administrasi Pasukan</span>
              <span className={`ml-1 px-2 py-0.5 rounded-full text-xs font-extrabold ${mainTab === 'administrasi_pasukan' ? currentGlobalTheme.badgeBg : 'bg-amber-400 text-slate-950 shadow-xs'}`}>
                Gudep & Regu
              </span>
            </button>
          </div>
        </div>
      </div>

      {/* TAB 1: WALI KELAS */}
      {mainTab === 'wali_kelas' && (
        <div className="space-y-6">
          {/* Class Selector Bar */}
          <div className="bg-white dark:bg-slate-800 rounded-2xl p-5 border border-slate-200 dark:border-slate-700/80 shadow-sm flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className={`p-3 rounded-xl ${currentGlobalTheme.badgeBg} shadow-xs`}>
                <Building2 size={22} />
              </div>
              <div>
                <h3 className="font-bold text-slate-800 dark:text-white text-base">
                  Pilih Kelas Perwalian (Wali Kelas)
                </h3>
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  Pilih kelas resmi yang menjadi tanggung jawab perwalian Anda.
                </p>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3 w-full md:w-auto">
              <div className="relative flex-1 md:w-56">
                <select
                  value={selectedHomeroomClass}
                  disabled={isClassLocked}
                  onChange={(e) => handleSelectHomeroomClass(e.target.value)}
                  className={`w-full px-4 py-2.5 rounded-xl border text-slate-800 dark:text-white font-bold text-sm outline-none transition-all ${
                    isClassLocked
                      ? 'bg-slate-100 dark:bg-slate-900/90 border-slate-200 dark:border-slate-800 cursor-not-allowed opacity-80'
                      : 'bg-slate-50 dark:bg-slate-900 border-slate-300 dark:border-slate-700 focus:ring-2 focus:ring-emerald-500'
                  }`}
                >
                  <option value="">-- Pilih Kelas --</option>
                  {classes.map((cls) => (
                    <option key={cls} value={cls}>
                      Kelas {cls}
                    </option>
                  ))}
                </select>
              </div>

              {isClassLocked && (
                <div className="flex items-center gap-1.5 px-3 py-2 bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-900/60 rounded-xl text-amber-700 dark:text-amber-300 text-xs font-bold shrink-0">
                  <Lock size={14} className="text-amber-500 shrink-0" />
                  <span>Terkunci (Hanya Admin)</span>
                </div>
              )}

              <button
                onClick={handleOpenAddStudent}
                disabled={!selectedHomeroomClass}
                className="inline-flex items-center gap-2 px-4 py-2.5 bg-indigo-600 hover:bg-indigo-700 disabled:bg-slate-300 text-white text-sm font-bold rounded-xl shadow-md transition-all shrink-0 cursor-pointer disabled:cursor-not-allowed"
              >
                <UserPlus size={18} />
                <span>Tambah Siswa</span>
              </button>

              <button
                onClick={handlePrintWkRoster}
                disabled={!selectedHomeroomClass || homeroomStudents.length === 0}
                className="inline-flex items-center gap-2 px-4 py-2.5 bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-300 text-white text-sm font-bold rounded-xl shadow-md transition-all shrink-0 cursor-pointer disabled:cursor-not-allowed"
              >
                <Printer size={18} />
                <span>Cetak Roster</span>
              </button>

              <button
                onClick={handleExportWkExcel}
                disabled={!selectedHomeroomClass || homeroomStudents.length === 0}
                className="inline-flex items-center gap-2 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-300 text-white text-sm font-bold rounded-xl shadow-md transition-all shrink-0 cursor-pointer disabled:cursor-not-allowed"
              >
                <Download size={18} />
                <span>Export Excel</span>
              </button>
            </div>
          </div>

          {/* Sub-Tab Navigation Bar: Revamped Modern Bento Card Grid */}
          {selectedHomeroomClass && (
            <div className="bg-slate-50/80 dark:bg-slate-900/60 p-3 sm:p-4 rounded-2xl border border-slate-200/80 dark:border-slate-800/80 shadow-2xs space-y-3">
              {/* Header Info */}
              <div className="flex flex-wrap items-center justify-between gap-2 px-1">
                <div className="flex items-center gap-2">
                  <div className="w-2.5 h-2.5 rounded-full bg-emerald-500 animate-pulse" />
                  <span className="text-xs font-black text-slate-800 dark:text-slate-200 tracking-wide uppercase">
                    Modul Administrasi Kelas: <span className="text-indigo-600 dark:text-indigo-400 font-black">Kelas {selectedHomeroomClass}</span>
                  </span>
                </div>
                <div className="flex items-center gap-2 text-[11px] font-bold text-slate-500 dark:text-slate-400">
                  <span className="px-2.5 py-0.5 rounded-full bg-slate-200/70 dark:bg-slate-800 text-slate-700 dark:text-slate-300 font-extrabold">
                    {homeroomStudents.length} Peserta Didik
                  </span>
                  <span className="hidden sm:inline">• 7 Modul Tersedia</span>
                </div>
              </div>

              {/* Bento Navigation Grid */}
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-7 gap-2.5 sm:gap-3">
                {[
                  {
                    id: 'database' as const,
                    title: 'Database Siswa',
                    subtitle: 'Data induk & biodata',
                    icon: UserCheck,
                    badge: `${homeroomStudents.length} Siswa`,
                    activeBg: 'bg-gradient-to-br from-emerald-500 via-teal-500 to-emerald-600 shadow-emerald-500/30 ring-emerald-400/50',
                    inactiveBorder: 'hover:border-emerald-400 dark:hover:border-emerald-500',
                    iconInactive: 'bg-emerald-50 dark:bg-emerald-950/60 text-emerald-600 dark:text-emerald-400',
                    badgeInactive: 'bg-emerald-50 dark:bg-emerald-950/50 text-emerald-700 dark:text-emerald-300 border-emerald-200/70 dark:border-emerald-800/60',
                    textHover: 'group-hover:text-emerald-600 dark:group-hover:text-emerald-400',
                  },
                  {
                    id: 'kehadiran' as const,
                    title: 'Presensi Kelas',
                    subtitle: 'Absensi harian & jam',
                    icon: CalendarDays,
                    badge: 'Presensi',
                    activeBg: 'bg-gradient-to-br from-blue-500 via-cyan-500 to-blue-600 shadow-blue-500/30 ring-blue-400/50',
                    inactiveBorder: 'hover:border-blue-400 dark:hover:border-blue-500',
                    iconInactive: 'bg-blue-50 dark:bg-blue-950/60 text-blue-600 dark:text-blue-400',
                    badgeInactive: 'bg-blue-50 dark:bg-blue-950/50 text-blue-700 dark:text-blue-300 border-blue-200/70 dark:border-blue-800/60',
                    textHover: 'group-hover:text-blue-600 dark:group-hover:text-blue-400',
                  },
                  {
                    id: 'laporan-bulanan' as const,
                    title: 'Laporan Bulanan',
                    subtitle: 'Rekap absen & cetak',
                    icon: FileSpreadsheet,
                    badge: 'Rekap Resmi',
                    activeBg: 'bg-gradient-to-br from-indigo-500 via-purple-500 to-indigo-600 shadow-indigo-500/30 ring-indigo-400/50',
                    inactiveBorder: 'hover:border-indigo-400 dark:hover:border-indigo-500',
                    iconInactive: 'bg-indigo-50 dark:bg-indigo-950/60 text-indigo-600 dark:text-indigo-400',
                    badgeInactive: 'bg-indigo-50 dark:bg-indigo-950/50 text-indigo-700 dark:text-indigo-300 border-indigo-200/70 dark:border-indigo-800/60',
                    textHover: 'group-hover:text-indigo-600 dark:group-hover:text-indigo-400',
                  },
                  {
                    id: 'catatan' as const,
                    title: 'Jurnal Walas',
                    subtitle: 'Catatan & pembinaan',
                    icon: BookOpen,
                    badge: 'Jurnal Digital',
                    activeBg: 'bg-gradient-to-br from-amber-500 via-orange-500 to-amber-600 shadow-amber-500/30 ring-amber-400/50',
                    inactiveBorder: 'hover:border-amber-400 dark:hover:border-amber-500',
                    iconInactive: 'bg-amber-50 dark:bg-amber-950/60 text-amber-600 dark:text-amber-400',
                    badgeInactive: 'bg-amber-50 dark:bg-amber-950/50 text-amber-800 dark:text-amber-300 border-amber-200/70 dark:border-amber-800/60',
                    textHover: 'group-hover:text-amber-600 dark:group-hover:text-amber-400',
                  },
                  {
                    id: 'perkembangan' as const,
                    title: 'Perkembangan',
                    subtitle: 'Akademik Smt 1-6',
                    icon: TrendingUp,
                    badge: 'Sms 1-6',
                    activeBg: 'bg-gradient-to-br from-sky-500 via-indigo-500 to-sky-600 shadow-sky-500/30 ring-sky-400/50',
                    inactiveBorder: 'hover:border-sky-400 dark:hover:border-sky-500',
                    iconInactive: 'bg-sky-50 dark:bg-sky-950/60 text-sky-600 dark:text-sky-400',
                    badgeInactive: 'bg-sky-50 dark:bg-sky-950/50 text-sky-700 dark:text-sky-300 border-sky-200/70 dark:border-sky-800/60',
                    textHover: 'group-hover:text-sky-600 dark:group-hover:text-sky-400',
                  },
                  {
                    id: 'catatan-rapor' as const,
                    title: 'Catatan Rapor',
                    subtitle: 'Deskripsi otomatis',
                    icon: FileText,
                    badge: 'Generator',
                    activeBg: 'bg-gradient-to-br from-rose-500 via-pink-500 to-rose-600 shadow-rose-500/30 ring-rose-400/50',
                    inactiveBorder: 'hover:border-rose-400 dark:hover:border-rose-500',
                    iconInactive: 'bg-rose-50 dark:bg-rose-950/60 text-rose-600 dark:text-rose-400',
                    badgeInactive: 'bg-rose-50 dark:bg-rose-950/50 text-rose-700 dark:text-rose-300 border-rose-200/70 dark:border-rose-800/60',
                    textHover: 'group-hover:text-rose-600 dark:group-hover:text-rose-400',
                  },
                  {
                    id: 'extrakurikuler' as const,
                    title: 'Ekstrakurikuler',
                    subtitle: 'Keikutsertaan & nilai',
                    icon: Award,
                    badge: 'Nilai Rapor',
                    activeBg: 'bg-gradient-to-br from-violet-500 via-fuchsia-500 to-purple-600 shadow-purple-500/30 ring-purple-400/50',
                    inactiveBorder: 'hover:border-purple-400 dark:hover:border-purple-500',
                    iconInactive: 'bg-purple-50 dark:bg-purple-950/60 text-purple-600 dark:text-purple-400',
                    badgeInactive: 'bg-purple-50 dark:bg-purple-950/50 text-purple-700 dark:text-purple-300 border-purple-200/70 dark:border-purple-800/60',
                    textHover: 'group-hover:text-purple-600 dark:group-hover:text-purple-400',
                  },
                ].map((item) => {
                  const isActive = wkSubTab === item.id;
                  const Icon = item.icon;
                  return (
                    <button
                      key={item.id}
                      onClick={() => setWkSubTab(item.id)}
                      className={`group relative text-left p-3 sm:p-3.5 rounded-xl sm:rounded-2xl transition-all duration-200 cursor-pointer overflow-hidden flex flex-col justify-between min-h-[96px] sm:min-h-[106px] ${
                        isActive
                          ? `${item.activeBg} text-white shadow-lg ring-2 -translate-y-1`
                          : `bg-white dark:bg-slate-800/90 text-slate-700 dark:text-slate-200 border border-slate-200/90 dark:border-slate-700/80 ${item.inactiveBorder} hover:bg-slate-50/90 dark:hover:bg-slate-750 hover:-translate-y-0.5 hover:shadow-md`
                      }`}
                    >
                      {/* Top Row: Icon + Badge */}
                      <div className="flex items-center justify-between gap-1 w-full mb-2">
                        <div
                          className={`w-7 h-7 sm:w-8 sm:h-8 rounded-lg flex items-center justify-center transition-all ${
                            isActive
                              ? 'bg-white/20 text-white shadow-2xs'
                              : `${item.iconInactive} shadow-2xs`
                          }`}
                        >
                          <Icon size={16} className="shrink-0" />
                        </div>

                        <span
                          className={`text-[9px] font-black tracking-wider uppercase px-1.5 py-0.5 rounded-md border ${
                            isActive
                              ? 'bg-white/25 text-white border-white/30 backdrop-blur-xs'
                              : item.badgeInactive
                          }`}
                        >
                          {item.badge}
                        </span>
                      </div>

                      {/* Bottom Row: Title + Subtitle */}
                      <div>
                        <h4
                          className={`text-xs sm:text-sm font-extrabold tracking-tight truncate leading-tight transition-colors ${
                            isActive
                              ? 'text-white'
                              : `text-slate-800 dark:text-slate-100 ${item.textHover}`
                          }`}
                        >
                          {item.title}
                        </h4>
                        <p
                          className={`text-[10px] sm:text-[11px] font-medium truncate mt-0.5 ${
                            isActive ? 'text-white/85' : 'text-slate-500 dark:text-slate-400'
                          }`}
                        >
                          {item.subtitle}
                        </p>
                      </div>

                      {/* Active Corner Notch / Glow */}
                      {isActive && (
                        <div className="absolute top-0 right-0 w-2.5 h-2.5 bg-white/40 rounded-bl-full shadow-xs" />
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {!selectedHomeroomClass ? (
            <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900/50 rounded-2xl p-8 text-center">
              <Info size={40} className="mx-auto text-amber-500 mb-3" />
              <h3 className="font-bold text-slate-800 dark:text-white text-lg">
                Belum Memilih Kelas Perwalian
              </h3>
              <p className="text-sm text-slate-600 dark:text-slate-400 max-w-md mx-auto mt-1">
                Silakan pilih kelas perwalian Anda pada menu dropdown di atas untuk melihat database peserta didik resmi kelas tersebut.
              </p>
            </div>
          ) : wkSubTab === 'kehadiran' ? (
            <ClassAttendanceManager
              homeroomClass={selectedHomeroomClass}
              students={students}
              activeUser={activeUser}
              schoolSettings={schoolSettings}
              attendanceRecords={attendanceRecords}
              onUpdateAttendance={onUpdateAttendance}
              onUpdateSettings={onUpdateSettings}
              homeroomNotes={homeroomNotes}
              onSaveHomeroomNote={onSaveHomeroomNote}
              appTheme={appTheme}
              agendas={agendas}
              agendaAttendanceRecords={agendaAttendanceRecords}
              schedules={schedules}
            />
          ) : wkSubTab === 'laporan-bulanan' ? (
            <MonthlyClassReportManager
              homeroomClass={selectedHomeroomClass}
              students={students}
              activeUser={activeUser}
              schoolSettings={schoolSettings}
              attendanceRecords={attendanceRecords}
              homeroomNotes={homeroomNotes}
              onSaveHomeroomNote={onSaveHomeroomNote}
              appTheme={appTheme}
              agendas={agendas}
              agendaAttendanceRecords={agendaAttendanceRecords}
            />
          ) : wkSubTab === 'catatan' ? (
            <HomeroomJournalManager
              homeroomClass={selectedHomeroomClass}
              students={students}
              activeUser={activeUser}
              schoolSettings={schoolSettings}
              notes={homeroomNotes}
              onSaveNote={onSaveHomeroomNote}
              appTheme={appTheme}
            />
          ) : wkSubTab === 'perkembangan' ? (
            <StudentAcademicProgressManager
              homeroomClass={selectedHomeroomClass}
              students={students}
              academicProgress={academicProgress}
              onSaveAcademicProgress={onSaveAcademicProgress}
              activeUser={activeUser}
              schoolSettings={schoolSettings}
              appTheme={appTheme}
            />
          ) : wkSubTab === 'catatan-rapor' ? (
            <ReportCardNotesManager
              homeroomClass={selectedHomeroomClass}
              students={students}
              academicProgress={academicProgress}
              attendanceRecords={attendanceRecords}
              homeroomNotes={homeroomNotes}
              activeUser={activeUser}
              schoolSettings={schoolSettings}
              appTheme={appTheme}
            />
          ) : wkSubTab === 'extrakurikuler' ? (
            <ExtracurricularManager
              homeroomClass={selectedHomeroomClass}
              students={homeroomStudents}
              activeUser={activeUser}
              schoolSettings={schoolSettings}
              extracurriculars={extracurriculars}
              onSaveExtracurricular={onSaveExtracurricular}
              appTheme={appTheme}
            />
          ) : (
            <>
              {/* Statistics Cards */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <div className="bg-white dark:bg-slate-800 p-5 rounded-2xl border border-slate-200 dark:border-slate-700/80 shadow-sm flex items-center justify-between">
                  <div>
                    <div className="text-xs font-semibold uppercase text-slate-500 dark:text-slate-400 tracking-wider">
                      Total Siswa
                    </div>
                    <div className="text-2xl font-black text-slate-800 dark:text-white mt-1">
                      {wkStats.total}{' '}
                      <span className="text-xs font-normal text-slate-500">Siswa</span>
                    </div>
                  </div>
                  <div className="p-3 bg-emerald-50 dark:bg-emerald-950/50 text-emerald-600 dark:text-emerald-400 rounded-xl">
                    <Users size={22} />
                  </div>
                </div>

                <div className="bg-white dark:bg-slate-800 p-5 rounded-2xl border border-slate-200 dark:border-slate-700/80 shadow-sm flex items-center justify-between">
                  <div>
                    <div className="text-xs font-semibold uppercase text-slate-500 dark:text-slate-400 tracking-wider">
                      Laki-laki
                    </div>
                    <div className="text-2xl font-black text-blue-600 dark:text-blue-400 mt-1">
                      {wkStats.laki}{' '}
                      <span className="text-xs font-normal text-slate-500">Siswa</span>
                    </div>
                  </div>
                  <div className="p-3 bg-blue-50 dark:bg-blue-950/50 text-blue-600 dark:text-blue-400 rounded-xl">
                    <User size={22} />
                  </div>
                </div>

                <div className="bg-white dark:bg-slate-800 p-5 rounded-2xl border border-slate-200 dark:border-slate-700/80 shadow-sm flex items-center justify-between">
                  <div>
                    <div className="text-xs font-semibold uppercase text-slate-500 dark:text-slate-400 tracking-wider">
                      Perempuan
                    </div>
                    <div className="text-2xl font-black text-pink-600 dark:text-pink-400 mt-1">
                      {wkStats.perempuan}{' '}
                      <span className="text-xs font-normal text-slate-500">Siswa</span>
                    </div>
                  </div>
                  <div className="p-3 bg-pink-50 dark:bg-pink-950/50 text-pink-600 dark:text-pink-400 rounded-xl">
                    <User size={22} />
                  </div>
                </div>

                <div className="bg-white dark:bg-slate-800 p-5 rounded-2xl border border-slate-200 dark:border-slate-700/80 shadow-sm flex items-center justify-between">
                  <div>
                    <div className="text-xs font-semibold uppercase text-slate-500 dark:text-slate-400 tracking-wider">
                      KIP / PIP
                    </div>
                    <div className="text-2xl font-black text-amber-600 dark:text-amber-400 mt-1">
                      {wkStats.kipPip}{' '}
                      <span className="text-xs font-normal text-slate-500">Penerima</span>
                    </div>
                  </div>
                  <div className="p-3 bg-amber-50 dark:bg-amber-950/50 text-amber-600 dark:text-amber-400 rounded-xl">
                    <CreditCard size={22} />
                  </div>
                </div>

                <div className="bg-white dark:bg-slate-800 p-5 rounded-2xl border border-slate-200 dark:border-slate-700/80 shadow-sm flex items-center justify-between col-span-2 md:col-span-1">
                  <div>
                    <div className="text-xs font-semibold uppercase text-slate-500 dark:text-slate-400 tracking-wider">
                      Status Aktif
                    </div>
                    <div className="text-2xl font-black text-teal-600 dark:text-teal-400 mt-1">
                      {wkStats.aktif}{' '}
                      <span className="text-xs font-normal text-slate-500">Aktif</span>
                    </div>
                  </div>
                  <div className="p-3 bg-teal-50 dark:bg-teal-950/50 text-teal-600 dark:text-teal-400 rounded-xl">
                    <ShieldCheck size={22} />
                  </div>
                </div>
              </div>

              {/* General Wali Kelas Notes Card */}
              <div className="bg-white dark:bg-slate-800 rounded-2xl p-5 border border-slate-200 dark:border-slate-700/80 shadow-sm space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <FileText size={18} className="text-emerald-600 dark:text-emerald-400" />
                    <h3 className="font-bold text-slate-800 dark:text-white text-sm">
                      Catatan / Pengumuman Wali Kelas ({selectedHomeroomClass})
                    </h3>
                  </div>
                  {!isEditingNotes ? (
                    <button
                      onClick={() => setIsEditingNotes(true)}
                      className="text-xs font-bold text-emerald-600 hover:text-emerald-700 dark:text-emerald-400 flex items-center gap-1 bg-emerald-50 dark:bg-emerald-950/40 px-3 py-1.5 rounded-lg border border-emerald-200 dark:border-emerald-800"
                    >
                      <Edit3 size={14} /> Edit Catatan
                    </button>
                  ) : (
                    <div className="flex items-center gap-2">
                      <button
                        onClick={handleSaveNotes}
                        className="text-xs font-bold text-white bg-emerald-600 hover:bg-emerald-700 px-3 py-1.5 rounded-lg flex items-center gap-1"
                      >
                        <Check size={14} /> Simpan
                      </button>
                      <button
                        onClick={() => {
                          setNotesInput(classNotes);
                          setIsEditingNotes(false);
                        }}
                        className="text-xs font-bold text-slate-600 dark:text-slate-300 bg-slate-100 dark:bg-slate-700 px-3 py-1.5 rounded-lg"
                      >
                        Batal
                      </button>
                    </div>
                  )}
                </div>

                {isEditingNotes ? (
                  <textarea
                    value={notesInput}
                    onChange={(e) => setNotesInput(e.target.value)}
                    placeholder="Tuliskan catatan kelas, jadwal piket, perwakilan kelas, atau instruksi wali kelas di sini..."
                    rows={3}
                    className="w-full p-3 text-sm bg-slate-50 dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded-xl text-slate-800 dark:text-white outline-none focus:ring-2 focus:ring-emerald-500"
                  />
                ) : (
                  <div className="p-3 bg-slate-50 dark:bg-slate-900/60 rounded-xl text-xs sm:text-sm text-slate-700 dark:text-slate-300 whitespace-pre-wrap min-h-[50px] italic">
                    {classNotes || 'Belum ada catatan wali kelas. Klik "Edit Catatan" untuk membuat pengumuman atau catatan khusus.'}
                  </div>
                )}
              </div>

              {/* Table Filters & Search */}
              <div className="bg-white dark:bg-slate-800 rounded-2xl p-5 border border-slate-200 dark:border-slate-700/80 shadow-sm space-y-4">
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-4">
                  <div className="relative flex-1">
                    <Search size={18} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input
                      type="text"
                      value={wkSearchQuery}
                      onChange={(e) => setWkSearchQuery(e.target.value)}
                      placeholder="Cari nama, panggilan, NIS, NISN, NIK, KK, Ijazah SD, nama ortu..."
                      className="w-full pl-10 pr-4 py-2.5 text-sm bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl text-slate-800 dark:text-white focus:ring-2 focus:ring-emerald-500 outline-none"
                    />
                    {wkSearchQuery && (
                      <button
                        onClick={() => setWkSearchQuery('')}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                      >
                        <X size={16} />
                      </button>
                    )}
                  </div>

                  <div className="flex flex-wrap items-center gap-3">
                    <select
                      value={wkGenderFilter}
                      onChange={(e: any) => setWkGenderFilter(e.target.value)}
                      className="px-3 py-2.5 text-xs font-semibold bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl text-slate-700 dark:text-slate-300 outline-none focus:ring-2 focus:ring-emerald-500"
                    >
                      <option value="SEMUA">Semua Gender</option>
                      <option value="Laki-laki">Laki-laki</option>
                      <option value="Perempuan">Perempuan</option>
                    </select>

                    <select
                      value={wkStatusFilter}
                      onChange={(e: any) => setWkStatusFilter(e.target.value)}
                      className="px-3 py-2.5 text-xs font-semibold bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl text-slate-700 dark:text-slate-300 outline-none focus:ring-2 focus:ring-emerald-500"
                    >
                      <option value="SEMUA">Semua Status</option>
                      <option value="Aktif">Aktif</option>
                      <option value="Tidak Aktif">Tidak Aktif</option>
                    </select>

                    <select
                      value={wkKipFilter}
                      onChange={(e: any) => setWkKipFilter(e.target.value)}
                      className="px-3 py-2.5 text-xs font-semibold bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl text-slate-700 dark:text-slate-300 outline-none focus:ring-2 focus:ring-emerald-500"
                    >
                      <option value="SEMUA">Semua Status KIP/PIP</option>
                      <option value="Penerima KIP/PIP">Penerima KIP/PIP</option>
                      <option value="Bukan Penerima">Bukan Penerima</option>
                    </select>

                    <select
                      value={wkIncompleteFilter}
                      onChange={(e: any) => setWkIncompleteFilter(e.target.value)}
                      className="px-3 py-2.5 text-xs font-semibold bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-900/60 rounded-xl text-amber-800 dark:text-amber-300 outline-none focus:ring-2 focus:ring-amber-500"
                    >
                      <option value="SEMUA">Semua Kelengkapan Data</option>
                      <option value="BELUM_LENGKAP">Ada Data Belum Lengkap</option>
                      <option value="NISN">Belum Isi NISN</option>
                      <option value="NIK">Belum Isi NIK</option>
                      <option value="KK">Belum Isi No. KK</option>
                      <option value="ORTU">Belum Isi Nama Ortu / Wali</option>
                      <option value="HP">Belum Isi No. HP</option>
                      <option value="ALAMAT">Belum Isi Alamat</option>
                      <option value="IJAZAH">Belum Isi No. Ijazah SD</option>
                      <option value="FOTO">Belum Unggah Foto</option>
                      <option value="STUDI">Belum Isi Rencana Studi Lanjutan</option>
                    </select>
                  </div>
                </div>

                {/* Main Table / Mobile Cards */}
                <div>
                  {/* MOBILE VIEW CARDS (Visible on sm:hidden) */}
                  <div className="block sm:hidden divide-y divide-slate-100 dark:divide-slate-800 bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden shadow-sm mb-4">
                    {sortedFilteredWkStudents.length === 0 ? (
                      <div className="py-8 text-center text-xs text-slate-500 dark:text-slate-400">
                        Tidak ada siswa ditemukan di Kelas {selectedHomeroomClass}.
                      </div>
                    ) : (
                      sortedFilteredWkStudents.map((s, idx) => {
                        const roleData = classRoles[s.id];
                        const activeRole = s.classRole || roleData?.role;
                        const activeNote = s.notes || s.classRoleNote || roleData?.note;

                        const missingFields: string[] = [];
                        if (!s.nisn || s.nisn.trim() === '') missingFields.push('NISN');
                        if (!s.nik || s.nik.trim() === '') missingFields.push('NIK');
                        if (!s.familyCardNo || s.familyCardNo.trim() === '') missingFields.push('No KK');
                        if (!s.fatherName && !s.motherName && !s.guardianName) missingFields.push('Ortu');
                        if (!s.phone && !s.parentPhone) missingFields.push('HP');
                        if (!s.address || s.address.trim() === '') missingFields.push('Alamat');
                        if (!s.elementaryCertNo || s.elementaryCertNo.trim() === '') missingFields.push('Ijazah SD');
                        if (!s.photoUrl || s.photoUrl.trim() === '') missingFields.push('Foto');
                        if (!s.rencanaStudiLanjutan || s.rencanaStudiLanjutan.trim() === '' || !s.rencanaStudiDetail || s.rencanaStudiDetail.trim() === '') missingFields.push('Rencana Studi');

                        return (
                          <div key={`mob-db-${s.id}`} className="p-4 bg-white dark:bg-slate-800 space-y-3">
                            <div className="flex items-start justify-between gap-2">
                              <div className="flex items-center gap-2.5 min-w-0">
                                <span className="w-6 h-6 rounded-lg bg-emerald-100 dark:bg-emerald-950 text-emerald-800 dark:text-emerald-300 font-black text-xs flex items-center justify-center shrink-0">
                                  {idx + 1}
                                </span>
                                {s.photoUrl ? (
                                  <img
                                    src={s.photoUrl}
                                    alt={s.name}
                                    className="w-9 h-9 rounded-full object-cover border border-emerald-500/30 shrink-0"
                                  />
                                ) : (
                                  <div className="w-9 h-9 rounded-full bg-emerald-100 dark:bg-emerald-950/70 text-emerald-700 dark:text-emerald-300 font-bold flex items-center justify-center text-xs border border-emerald-500/20 shrink-0">
                                    {s.name.charAt(0).toUpperCase()}
                                  </div>
                                )}
                                <div className="min-w-0">
                                  <h4 className="font-bold text-sm text-slate-900 dark:text-white truncate">
                                    {s.name} {s.nickname ? `(${s.nickname})` : ''}
                                  </h4>
                                  <p className="text-[11px] text-slate-500 dark:text-slate-400">
                                    NIS: {s.nis || '-'} • <span className={s.gender === 'Laki-laki' ? 'text-blue-600 dark:text-blue-400' : 'text-pink-600 dark:text-pink-400'}>{s.gender}</span>
                                  </p>
                                </div>
                              </div>

                              <span className={`px-2 py-0.5 rounded-full text-[10px] font-black shrink-0 ${
                                s.status === 'Aktif' 
                                  ? 'bg-emerald-100 dark:bg-emerald-950/80 text-emerald-800 dark:text-emerald-300'
                                  : 'bg-rose-100 dark:bg-rose-950/80 text-rose-800 dark:text-rose-300'
                              }`}>
                                {s.status || 'Aktif'}
                              </span>
                            </div>

                            <div className="grid grid-cols-2 gap-2 text-xs bg-slate-50 dark:bg-slate-900/60 p-2.5 rounded-xl border border-slate-100 dark:border-slate-800">
                              <div>
                                <span className="text-[10px] text-slate-400 block font-bold">NISN / NIK / KK</span>
                                <span className="font-semibold text-slate-700 dark:text-slate-300 block truncate">
                                  {s.nisn || '-'} {s.nik ? `/ ${s.nik}` : ''}
                                </span>
                              </div>
                              <div>
                                <span className="text-[10px] text-slate-400 block font-bold">Orang Tua / HP</span>
                                <span className="font-semibold text-slate-700 dark:text-slate-300 block truncate">
                                  {s.fatherName || s.motherName || s.guardianName || '-'} {s.parentPhone ? `(${s.parentPhone})` : ''}
                                </span>
                              </div>
                            </div>

                            {(activeRole || (s.kipPipStatus && s.kipPipStatus !== 'Bukan Penerima') || s.rencanaStudiLanjutan || missingFields.length > 0) && (
                              <div className="flex items-center gap-1.5 flex-wrap text-[11px]">
                                {activeRole && (
                                  <span className="px-2 py-0.5 bg-amber-100 dark:bg-amber-950/80 text-amber-800 dark:text-amber-300 rounded font-black">
                                    {activeRole}
                                  </span>
                                )}
                                {s.kipPipStatus && s.kipPipStatus !== 'Bukan Penerima' && (
                                  <span className="px-2 py-0.5 bg-indigo-100 dark:bg-indigo-950/80 text-indigo-700 dark:text-indigo-300 rounded font-black">
                                    {s.kipPipStatus}
                                  </span>
                                )}
                                {s.rencanaStudiLanjutan && (
                                  <span className="px-2 py-0.5 bg-purple-100 dark:bg-purple-950/80 text-purple-700 dark:text-purple-300 rounded font-black">
                                    {s.rencanaStudiLanjutan}{s.rencanaStudiDetail ? `: ${s.rencanaStudiDetail}` : ''}
                                  </span>
                                )}
                                {missingFields.length > 0 && (
                                  <span className="text-[10px] bg-amber-50 dark:bg-amber-950/30 text-amber-600 dark:text-amber-400 px-2 py-0.5 rounded border border-amber-200/50">
                                    ⚠️ Kosong: {missingFields.join(', ')}
                                  </span>
                                )}
                              </div>
                            )}

                            <div className="flex items-center justify-between gap-2 pt-1 border-t border-slate-100 dark:border-slate-800">
                              <button
                                onClick={() => handleOpenStudentDetail(s, false)}
                                className="flex-1 py-1.5 bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 text-slate-700 dark:text-slate-200 text-xs font-bold rounded-lg transition-all flex items-center justify-center gap-1 cursor-pointer"
                              >
                                <Eye size={13} className="text-emerald-500" />
                                <span>Detail</span>
                              </button>
                              <button
                                onClick={() => handleOpenStudentDetail(s, true)}
                                className="flex-1 py-1.5 bg-indigo-50 dark:bg-indigo-950/50 hover:bg-indigo-100 text-indigo-700 dark:text-indigo-300 text-xs font-bold rounded-lg transition-all flex items-center justify-center gap-1 cursor-pointer"
                              >
                                <Edit3 size={13} />
                                <span>Edit DB</span>
                              </button>
                              <button
                                onClick={() => handleDeleteStudent(s.id)}
                                className="p-1.5 bg-rose-50 dark:bg-rose-950/50 hover:bg-rose-100 text-rose-600 dark:text-rose-400 rounded-lg transition-all cursor-pointer"
                                title="Hapus Siswa"
                              >
                                <Trash2 size={14} />
                              </button>
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>

                  {/* DESKTOP VIEW TABLE (Visible on hidden sm:block) */}
                  <div className="hidden sm:block w-full rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm bg-white dark:bg-slate-800 overflow-hidden">
                    <table className="w-full text-left text-[10.5px] xl:text-[11.5px] text-slate-700 dark:text-slate-200 border-collapse table-fixed">
                      <colgroup>
                        <col className="w-[3.5%]" />
                        <col className="w-[18%]" />
                        <col className="w-[12%]" />
                        <col className="w-[9%]" />
                        <col className="w-[11%]" />
                        <col className="w-[15.5%]" />
                        <col className="w-[11%]" />
                        <col className="w-[12%]" />
                        <col className="w-[8%]" />
                      </colgroup>
                      <thead className="bg-slate-100 dark:bg-slate-900/80 font-bold uppercase text-slate-600 dark:text-slate-400 border-b border-slate-200 dark:border-slate-700">
                        <tr>
                          <th className="py-2.5 px-1 text-center">No</th>
                          <th className="py-2.5 px-1.5 xl:px-2">
                            {renderWkSortHeader('Peserta Didik (Nama)', 'name')}
                          </th>
                          <th className="py-2.5 px-1.5 xl:px-2">
                            {renderWkSortHeader('Identitas (NIS / NISN)', 'nis')}
                          </th>
                          <th className="py-2.5 px-1 text-center">
                            {renderWkSortHeader('L/P & Status', 'gender', 'center')}
                          </th>
                          <th className="py-2.5 px-1.5 xl:px-2">
                            {renderWkSortHeader('No. HP Siswa', 'phone')}
                          </th>
                          <th className="py-2.5 px-1.5 xl:px-2">
                            {renderWkSortHeader('Nama Orang Tua / Wali', 'parent')}
                          </th>
                          <th className="py-2.5 px-1.5 xl:px-2">
                            {renderWkSortHeader('No. HP Ortu', 'parentPhone')}
                          </th>
                          <th className="py-2.5 px-1.5 xl:px-2">
                            {renderWkSortHeader('Jabatan & KIP/PIP', 'role')}
                          </th>
                          <th className="py-2.5 px-1 text-center">Aksi</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-200 dark:divide-slate-700/60 bg-white dark:bg-slate-800">
                        {sortedFilteredWkStudents.length === 0 ? (
                          <tr>
                            <td colSpan={9} className="py-8 text-center text-slate-500 dark:text-slate-400">
                              Tidak ada siswa ditemukan di Kelas {selectedHomeroomClass}.
                            </td>
                          </tr>
                        ) : (
                          sortedFilteredWkStudents.map((s, idx) => {
                            const roleData = classRoles[s.id];
                            const activeRole = s.classRole || roleData?.role;
                            const activeNote = s.notes || s.classRoleNote || roleData?.note;
                            const isEditingThis = editingRoleId === s.id;

                            const missingFields: string[] = [];
                            if (!s.nisn || s.nisn.trim() === '') missingFields.push('NISN');
                            if (!s.nik || s.nik.trim() === '') missingFields.push('NIK');
                            if (!s.familyCardNo || s.familyCardNo.trim() === '') missingFields.push('No KK');
                            if (!s.fatherName && !s.motherName && !s.guardianName) missingFields.push('Ortu');
                            if (!s.phone) missingFields.push('HP Siswa');
                            if (!s.parentPhone) missingFields.push('HP Ortu');
                            if (!s.address || s.address.trim() === '') missingFields.push('Alamat');
                            if (!s.elementaryCertNo || s.elementaryCertNo.trim() === '') missingFields.push('Ijazah SD');
                            if (!s.photoUrl || s.photoUrl.trim() === '') missingFields.push('Foto');
                            if (!s.rencanaStudiLanjutan || s.rencanaStudiLanjutan.trim() === '' || !s.rencanaStudiDetail || s.rencanaStudiDetail.trim() === '') missingFields.push('Rencana Studi');

                            return (
                              <tr key={s.id} className="hover:bg-slate-50/80 dark:hover:bg-slate-700/40 transition-colors">
                                <td className="py-2 px-1 text-center font-bold text-slate-500 text-[10px] xl:text-[11px] align-top">
                                  {idx + 1}
                                </td>

                                {/* Student Photo & Name */}
                                <td className="py-2 px-1.5 xl:px-2 align-top">
                                  <div className="flex items-start gap-1.5">
                                    <div className="relative shrink-0 mt-0.5">
                                      {s.photoUrl ? (
                                        <img
                                          src={s.photoUrl}
                                          alt={s.name}
                                          className="w-6 h-6 xl:w-7 xl:h-7 rounded-full object-cover border border-emerald-500/30"
                                        />
                                      ) : (
                                        <div className="w-6 h-6 xl:w-7 xl:h-7 rounded-full bg-emerald-100 dark:bg-emerald-950/70 text-emerald-700 dark:text-emerald-300 font-bold flex items-center justify-center text-[10px] xl:text-xs border border-emerald-500/20">
                                          {s.name.charAt(0).toUpperCase()}
                                        </div>
                                      )}
                                    </div>
                                    <div className="min-w-0 flex-1">
                                      <div className="font-bold text-slate-900 dark:text-white leading-tight break-words text-[11px] xl:text-xs">
                                        <span>{s.name}</span>
                                        {s.nickname && (
                                          <span className="text-[10px] font-normal text-slate-400 ml-1">
                                            ({s.nickname})
                                          </span>
                                        )}
                                      </div>
                                      {s.address && (
                                        <div className="text-[9.5px] text-slate-400 flex items-start gap-0.5 mt-0.5 break-words leading-tight">
                                          <MapPin size={9} className="shrink-0 mt-0.5 text-slate-400" />
                                          <span className="break-words">
                                            {s.address}
                                            {s.postalCode ? ` (Kode Pos: ${s.postalCode})` : ''}
                                          </span>
                                        </div>
                                      )}
                                      {missingFields.length > 0 && (
                                        <div className="text-[8.5px] text-amber-600 dark:text-amber-400 font-medium flex items-start gap-1 mt-0.5 bg-amber-50 dark:bg-amber-950/30 px-1 py-0.5 rounded border border-amber-200/50 dark:border-amber-900/30 leading-tight">
                                          <span className="font-bold shrink-0">⚠️ Kosong:</span>
                                          <span className="break-words">{missingFields.join(', ')}</span>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </td>

                                {/* Identitas NIS/NISN/NIK/KK */}
                                <td className="py-2 px-1.5 xl:px-2 text-[10px] xl:text-[11px] font-mono text-slate-600 dark:text-slate-300 align-top break-words">
                                  <div className="leading-tight space-y-0.5">
                                    <div><span className="text-slate-400 font-sans text-[9.5px]">NIS:</span> <span className="font-semibold">{s.nis || '-'}</span></div>
                                    {s.nisn && <div><span className="text-slate-400 font-sans text-[9.5px]">NISN:</span> <span className="font-semibold break-all">{s.nisn}</span></div>}
                                    {s.nik && <div className="text-[9.5px] text-slate-500 dark:text-slate-400 break-all"><span className="font-sans">NIK:</span> {s.nik}</div>}
                                    {s.familyCardNo && <div className="text-[9.5px] text-slate-500 dark:text-slate-400 break-all"><span className="font-sans">KK:</span> {s.familyCardNo}</div>}
                                  </div>
                                </td>

                                {/* Gender & Status */}
                                <td className="py-2 px-1 text-center align-top">
                                  <div className="flex flex-col items-center gap-1">
                                    <span
                                      className={`inline-block px-1 py-0.5 rounded text-[9.5px] font-bold leading-none break-words ${
                                        s.gender === 'Laki-laki'
                                          ? 'bg-blue-50 text-blue-600 dark:bg-blue-950/60 dark:text-blue-400'
                                          : 'bg-pink-50 text-pink-600 dark:bg-pink-950/60 dark:text-pink-400'
                                      }`}
                                    >
                                      {s.gender === 'Laki-laki' ? 'Laki-laki' : 'Perempuan'}
                                    </span>
                                    <span
                                      className={`inline-block px-1.5 py-0.5 rounded-full text-[8.5px] font-extrabold leading-none break-words ${
                                        s.status === 'Aktif'
                                          ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-400'
                                          : 'bg-rose-100 text-rose-800 dark:bg-rose-950/60 dark:text-rose-400'
                                      }`}
                                    >
                                      {s.status || 'Aktif'}
                                    </span>
                                  </div>
                                </td>

                                {/* No. HP Siswa */}
                                <td className="py-2 px-1.5 xl:px-2 text-[10px] xl:text-[11px] font-mono align-top">
                                  {s.phone ? (
                                    <div className="flex items-start gap-1 text-slate-700 dark:text-slate-300 leading-tight">
                                      <Phone size={10} className="text-emerald-500 shrink-0 mt-0.5" />
                                      <span className="break-all">{s.phone}</span>
                                    </div>
                                  ) : (
                                    <span className="text-slate-400 italic text-[9.5px]">Belum diisi</span>
                                  )}
                                </td>

                                {/* Nama Orang Tua / Wali */}
                                <td className="py-2 px-1.5 xl:px-2 text-[10.5px] xl:text-[11px] leading-tight align-top">
                                  <div className="text-slate-700 dark:text-slate-300 font-medium space-y-0.5">
                                    {(s.fatherName || s.motherName) ? (
                                      <div>
                                        {s.fatherName && <div className="break-words"><span className="text-slate-400 text-[9.5px]">Ayah:</span> {s.fatherName}</div>}
                                        {s.motherName && <div className="break-words"><span className="text-slate-400 text-[9.5px]">Ibu:</span> {s.motherName}</div>}
                                      </div>
                                    ) : s.guardianName ? (
                                      <div className="break-words"><span className="text-slate-400 text-[9.5px]">Wali:</span> {s.guardianName}</div>
                                    ) : (
                                      <div className="text-slate-400 font-normal italic text-[9.5px]">Ortu belum diisi</div>
                                    )}
                                  </div>
                                </td>

                                {/* No. HP Orang Tua */}
                                <td className="py-2 px-1.5 xl:px-2 text-[10px] xl:text-[11px] font-mono align-top">
                                  {s.parentPhone ? (
                                    <div className="flex items-start gap-1 text-slate-700 dark:text-slate-300 leading-tight">
                                      <Phone size={10} className="text-indigo-500 shrink-0 mt-0.5" />
                                      <span className="break-all">{s.parentPhone}</span>
                                    </div>
                                  ) : (
                                    <span className="text-slate-400 italic text-[9.5px]">Belum diisi</span>
                                  )}
                                </td>

                                {/* Jabatan & Catatan / KIP */}
                                <td className="py-2 px-1.5 xl:px-2 align-top">
                                  {isEditingThis ? (
                                    <div className="flex flex-col gap-1 w-full">
                                      <input
                                        type="text"
                                        placeholder="Jabatan"
                                        value={inputRole}
                                        onChange={(e) => setInputRole(e.target.value)}
                                        className="w-full px-1.5 py-0.5 text-[9.5px] bg-slate-50 dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded outline-none focus:ring-1 focus:ring-emerald-500"
                                      />
                                      <input
                                        type="text"
                                        placeholder="Catatan"
                                        value={inputNote}
                                        onChange={(e) => setInputNote(e.target.value)}
                                        className="w-full px-1.5 py-0.5 text-[9.5px] bg-slate-50 dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded outline-none focus:ring-1 focus:ring-emerald-500"
                                      />
                                    </div>
                                  ) : (
                                    <div className="flex flex-wrap items-center gap-1">
                                      {activeRole && (
                                        <span className="inline-flex items-center gap-0.5 px-1 py-0.5 rounded bg-amber-50 dark:bg-amber-950/50 text-amber-700 dark:text-amber-300 font-bold text-[9.5px] border border-amber-200 dark:border-amber-800 leading-tight break-words">
                                          <Award size={9} className="shrink-0" /> {activeRole}
                                        </span>
                                      )}
                                      {s.kipPipStatus && s.kipPipStatus !== 'Bukan Penerima' && (
                                        <span className="inline-flex items-center gap-0.5 px-1 py-0.5 rounded bg-emerald-50 dark:bg-emerald-950/50 text-emerald-700 dark:text-emerald-300 font-bold text-[9.5px] border border-emerald-200 dark:border-emerald-800 leading-tight break-words">
                                          <CreditCard size={9} className="shrink-0" /> {s.kipPipStatus}
                                        </span>
                                      )}
                                      {s.rencanaStudiLanjutan && (
                                        <span className="inline-flex items-center gap-0.5 px-1 py-0.5 rounded bg-purple-50 dark:bg-purple-950/50 text-purple-700 dark:text-purple-300 font-bold text-[9.5px] border border-purple-200 dark:border-purple-800 leading-tight break-words" title={`Rencana Studi: ${s.rencanaStudiLanjutan}${s.rencanaStudiDetail ? ` - ${s.rencanaStudiDetail}` : ''}`}>
                                          <BookOpen size={9} className="shrink-0" /> {s.rencanaStudiLanjutan}{s.rencanaStudiDetail ? `: ${s.rencanaStudiDetail}` : ''}
                                        </span>
                                      )}
                                      {!activeRole && (!s.kipPipStatus || s.kipPipStatus === 'Bukan Penerima') && !s.rencanaStudiLanjutan && (
                                        <span className="text-[9.5px] text-slate-400 italic">Tidak ada</span>
                                      )}
                                      {activeNote && (
                                        <div className="w-full text-[9px] text-slate-500 dark:text-slate-400 break-words leading-tight" title={activeNote}>
                                          Note: {activeNote}
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </td>

                                {/* Aksi */}
                                <td className="py-2 px-0.5 text-center align-top">
                                  <div className="flex items-center justify-center gap-0.5 xl:gap-1 flex-wrap">
                                    {/* Full Detail / Edit Modal Trigger */}
                                    <button
                                      onClick={() => handleOpenStudentDetail(s, false)}
                                      className="p-1 text-slate-600 dark:text-slate-300 hover:text-emerald-600 dark:hover:text-emerald-400 bg-slate-100 dark:bg-slate-700/60 rounded-md hover:bg-emerald-50 dark:hover:bg-emerald-950/50 transition-colors"
                                      title="Lihat Data Lengkap Siswa"
                                    >
                                      <Eye size={12} />
                                    </button>

                                    <button
                                      onClick={() => handleOpenStudentDetail(s, true)}
                                      className="p-1 text-slate-600 dark:text-slate-300 hover:text-indigo-600 dark:hover:text-indigo-400 bg-slate-100 dark:bg-slate-700/60 rounded-md hover:bg-indigo-50 dark:hover:bg-indigo-950/50 transition-colors"
                                      title="Edit Database Siswa"
                                    >
                                      <Edit3 size={12} />
                                    </button>

                                    <button
                                      onClick={() => handleDeleteStudent(s.id)}
                                      className="p-1 text-slate-400 hover:text-rose-600 rounded-md hover:bg-rose-50 dark:hover:bg-rose-950/50 transition-colors"
                                      title="Hapus Siswa"
                                    >
                                      <Trash2 size={12} />
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            );
                          })
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* TAB 2: WALI ROMBEL */}
      {mainTab === 'wali_rombel' && (
        <div>
          <WaliRombelView
            waliRombel={waliRombel}
            students={students}
            classes={classes}
            activeUser={activeUser}
            schoolSettings={schoolSettings}
            onSaveWali={onSaveWali}
            appTheme={appTheme}
          />
        </div>
      )}

      {/* TAB 3: ADMINISTRASI PASUKAN */}
      {mainTab === 'administrasi_pasukan' && (
        <div className="space-y-6">
          <PramukaAdministrationManager
            homeroomClass={selectedHomeroomClass}
            students={homeroomStudents}
            allStudents={students}
            classes={classes}
            onSelectHomeroomClass={handleSelectHomeroomClass}
            activeUser={activeUser}
            schoolSettings={schoolSettings}
            appTheme={appTheme}
            isTopLevelView={true}
          />
        </div>
      )}

      {/* MODAL FULL DATABASE PESERTA DIDIK (DETAIL & EDIT) */}
      {selectedStudentDetail && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm overflow-y-auto">
          <div className="bg-white dark:bg-slate-800 w-full max-w-3xl rounded-3xl shadow-2xl border border-slate-200 dark:border-slate-700 overflow-hidden my-8 max-h-[90vh] flex flex-col">
            
            {/* Modal Header */}
            <div className={`bg-gradient-to-r ${currentGlobalTheme.cardGradients[0] || currentGlobalTheme.accentGradient} p-6 text-white relative shrink-0`}>
              <button
                onClick={() => setSelectedStudentDetail(null)}
                className="absolute right-4 top-4 p-2 text-white/80 hover:text-white rounded-full bg-black/20 hover:bg-black/40 transition-colors"
              >
                <X size={20} />
              </button>

              <div className="flex items-center gap-4">
                <div className="relative shrink-0">
                  {detailFormData.photoUrl ? (
                    <img
                      src={detailFormData.photoUrl}
                      alt={detailFormData.name || 'Siswa'}
                      className="w-16 h-16 rounded-2xl object-cover border-2 border-white/40 shadow-lg"
                    />
                  ) : (
                    <div className="w-16 h-16 rounded-2xl bg-white/10 backdrop-blur-md text-white font-black text-2xl flex items-center justify-center border-2 border-white/20">
                      {(detailFormData.name || 'S').charAt(0).toUpperCase()}
                    </div>
                  )}
                  {isEditingDetail && (
                    <label className="absolute -bottom-1 -right-1 p-1.5 bg-indigo-600 text-white rounded-lg shadow cursor-pointer hover:bg-indigo-700">
                      <Camera size={14} />
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handlePhotoUpload}
                        className="hidden"
                      />
                    </label>
                  )}
                </div>

                <div>
                  <div className="flex items-center gap-2">
                    <span className="px-2.5 py-0.5 rounded-full bg-white/20 text-xs font-bold backdrop-blur-md">
                      Kelas {detailFormData.class || selectedHomeroomClass}
                    </span>
                    <span
                      className={`px-2.5 py-0.5 rounded-full text-xs font-bold ${
                        detailFormData.status === 'Aktif'
                          ? 'bg-emerald-400 text-emerald-950'
                          : 'bg-rose-400 text-rose-950'
                      }`}
                    >
                      {detailFormData.status || 'Aktif'}
                    </span>
                  </div>
                  <h2 className="text-xl font-black mt-1">
                    {detailFormData.name || 'Form Peserta Didik Baru'}
                  </h2>
                  <p className="text-xs text-emerald-100">
                    NIS: {detailFormData.nis || '-'} | NISN: {detailFormData.nisn || '-'} | NIK: {detailFormData.nik || '-'} | KK: {detailFormData.familyCardNo || '-'}
                  </p>
                </div>
              </div>

              {/* Modal Tabs */}
              <div className="flex items-center gap-2 mt-6 border-b border-white/10 pb-0">
                <button
                  onClick={() => setDetailTab('identitas')}
                  className={`px-4 py-2 text-xs font-bold rounded-t-xl transition-all ${
                    detailTab === 'identitas'
                      ? 'bg-white text-emerald-900 font-extrabold shadow-sm'
                      : 'text-emerald-100 hover:bg-white/10'
                  }`}
                >
                  1. Data Identitas
                </button>
                <button
                  onClick={() => setDetailTab('orangtua')}
                  className={`px-4 py-2 text-xs font-bold rounded-t-xl transition-all ${
                    detailTab === 'orangtua'
                      ? 'bg-white text-emerald-900 font-extrabold shadow-sm'
                      : 'text-emerald-100 hover:bg-white/10'
                  }`}
                >
                  2. Data Orang Tua / Wali
                </button>
                <button
                  onClick={() => setDetailTab('pendukung')}
                  className={`px-4 py-2 text-xs font-bold rounded-t-xl transition-all ${
                    detailTab === 'pendukung'
                      ? 'bg-white text-emerald-900 font-extrabold shadow-sm'
                      : 'text-emerald-100 hover:bg-white/10'
                  }`}
                >
                  3. Data Pendukung & Dokumen
                </button>
              </div>
            </div>

            {/* Modal Body */}
            <div className="p-6 overflow-y-auto flex-1 space-y-4 text-slate-800 dark:text-slate-100">
              
              {/* TAB 1: DATA IDENTITAS */}
              {detailTab === 'identitas' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-bold text-slate-600 dark:text-slate-400 mb-1">
                      Nama Lengkap *
                    </label>
                    <input
                      type="text"
                      disabled={!isEditingDetail}
                      value={detailFormData.name || ''}
                      onChange={(e) => setDetailFormData({ ...detailFormData, name: e.target.value })}
                      placeholder="Masukkan nama lengkap siswa"
                      className="w-full px-3.5 py-2 text-sm bg-slate-50 dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded-xl outline-none focus:ring-2 focus:ring-emerald-500 disabled:opacity-80"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-slate-600 dark:text-slate-400 mb-1">
                      Nama Panggilan
                    </label>
                    <input
                      type="text"
                      disabled={!isEditingDetail}
                      value={detailFormData.nickname || ''}
                      onChange={(e) => setDetailFormData({ ...detailFormData, nickname: e.target.value })}
                      placeholder="Nama panggilan"
                      className="w-full px-3.5 py-2 text-sm bg-slate-50 dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded-xl outline-none focus:ring-2 focus:ring-emerald-500 disabled:opacity-80"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-slate-600 dark:text-slate-400 mb-1">
                      NIS (Nomor Induk Siswa)
                    </label>
                    <input
                      type="text"
                      disabled={!isEditingDetail}
                      value={detailFormData.nis || ''}
                      onChange={(e) => setDetailFormData({ ...detailFormData, nis: e.target.value })}
                      placeholder="NIS sekolah"
                      className="w-full px-3.5 py-2 text-sm bg-slate-50 dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded-xl outline-none focus:ring-2 focus:ring-emerald-500 disabled:opacity-80"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-slate-600 dark:text-slate-400 mb-1">
                      NISN (Nomor Induk Siswa Nasional)
                    </label>
                    <input
                      type="text"
                      disabled={!isEditingDetail}
                      value={detailFormData.nisn || ''}
                      onChange={(e) => setDetailFormData({ ...detailFormData, nisn: e.target.value })}
                      placeholder="NISN nasional"
                      className="w-full px-3.5 py-2 text-sm bg-slate-50 dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded-xl outline-none focus:ring-2 focus:ring-emerald-500 disabled:opacity-80"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-slate-600 dark:text-slate-400 mb-1">
                      NIK (Nomor Induk Kependudukan)
                    </label>
                    <input
                      type="text"
                      disabled={!isEditingDetail}
                      value={detailFormData.nik || ''}
                      onChange={(e) => setDetailFormData({ ...detailFormData, nik: e.target.value })}
                      placeholder="NIK siswa (16 digit)"
                      className="w-full px-3.5 py-2 text-sm bg-slate-50 dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded-xl outline-none focus:ring-2 focus:ring-emerald-500 disabled:opacity-80"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-slate-600 dark:text-slate-400 mb-1">
                      Nomor Kartu Keluarga (KK)
                    </label>
                    <input
                      type="text"
                      disabled={!isEditingDetail}
                      value={detailFormData.familyCardNo || ''}
                      onChange={(e) => setDetailFormData({ ...detailFormData, familyCardNo: e.target.value })}
                      placeholder="Nomor Kartu Keluarga (16 digit)"
                      className="w-full px-3.5 py-2 text-sm bg-slate-50 dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded-xl outline-none focus:ring-2 focus:ring-emerald-500 disabled:opacity-80"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-slate-600 dark:text-slate-400 mb-1">
                      Nomor Ijazah SD/MI
                    </label>
                    <input
                      type="text"
                      disabled={!isEditingDetail}
                      value={detailFormData.elementaryCertNo || ''}
                      onChange={(e) => setDetailFormData({ ...detailFormData, elementaryCertNo: e.target.value })}
                      placeholder="Nomor Ijazah SD/MI"
                      className="w-full px-3.5 py-2 text-sm bg-slate-50 dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded-xl outline-none focus:ring-2 focus:ring-emerald-500 disabled:opacity-80"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-slate-600 dark:text-slate-400 mb-1">
                      Jenis Kelamin
                    </label>
                    <select
                      disabled={!isEditingDetail}
                      value={detailFormData.gender || 'Laki-laki'}
                      onChange={(e: any) => setDetailFormData({ ...detailFormData, gender: e.target.value })}
                      className="w-full px-3.5 py-2 text-sm bg-slate-50 dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded-xl outline-none focus:ring-2 focus:ring-emerald-500 disabled:opacity-80"
                    >
                      <option value="Laki-laki">Laki-laki</option>
                      <option value="Perempuan">Perempuan</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-slate-600 dark:text-slate-400 mb-1">
                      Tempat Lahir
                    </label>
                    <input
                      type="text"
                      disabled={!isEditingDetail}
                      value={detailFormData.pob || ''}
                      onChange={(e) => setDetailFormData({ ...detailFormData, pob: e.target.value })}
                      placeholder="Kota/Kabupaten lahir"
                      className="w-full px-3.5 py-2 text-sm bg-slate-50 dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded-xl outline-none focus:ring-2 focus:ring-emerald-500 disabled:opacity-80"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-slate-600 dark:text-slate-400 mb-1">
                      Tanggal Lahir
                    </label>
                    <input
                      type="date"
                      disabled={!isEditingDetail}
                      value={detailFormData.dob || ''}
                      onChange={(e) => setDetailFormData({ ...detailFormData, dob: e.target.value })}
                      className="w-full px-3.5 py-2 text-sm bg-slate-50 dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded-xl outline-none focus:ring-2 focus:ring-emerald-500 disabled:opacity-80"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-slate-600 dark:text-slate-400 mb-1">
                      Agama
                    </label>
                    <select
                      disabled={!isEditingDetail}
                      value={detailFormData.religion || 'Islam'}
                      onChange={(e) => setDetailFormData({ ...detailFormData, religion: e.target.value })}
                      className="w-full px-3.5 py-2 text-sm bg-slate-50 dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded-xl outline-none focus:ring-2 focus:ring-emerald-500 disabled:opacity-80"
                    >
                      <option value="Islam">Islam</option>
                      <option value="Kristen">Kristen</option>
                      <option value="Katholik">Katholik</option>
                      <option value="Hindu">Hindu</option>
                      <option value="Buddha">Buddha</option>
                      <option value="Khonghucu">Khonghucu</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-slate-600 dark:text-slate-400 mb-1">
                      Nomor HP Peserta Didik
                    </label>
                    <input
                      type="text"
                      disabled={!isEditingDetail}
                      value={detailFormData.phone || ''}
                      onChange={(e) => setDetailFormData({ ...detailFormData, phone: e.target.value })}
                      placeholder="08xxxxxxxxxx"
                      className="w-full px-3.5 py-2 text-sm bg-slate-50 dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded-xl outline-none focus:ring-2 focus:ring-emerald-500 disabled:opacity-80"
                    />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 md:col-span-2">
                    <div className="sm:col-span-2">
                      <label className="block text-xs font-bold text-slate-600 dark:text-slate-400 mb-1">
                        Alamat Tempat Tinggal (Siswa)
                      </label>
                      <textarea
                        disabled={!isEditingDetail}
                        rows={2}
                        value={detailFormData.address || ''}
                        onChange={(e) => setDetailFormData({ ...detailFormData, address: e.target.value })}
                        placeholder="Alamat lengkap (Dusun, RT/RW, Desa, Kecamatan)"
                        className="w-full px-3.5 py-2 text-sm bg-slate-50 dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded-xl outline-none focus:ring-2 focus:ring-emerald-500 disabled:opacity-80 resize-none"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-slate-600 dark:text-slate-400 mb-1 flex items-center gap-1">
                        <MapPin size={12} className="text-emerald-500" /> Kode Pos Siswa
                      </label>
                      <input
                        type="text"
                        disabled={!isEditingDetail}
                        value={detailFormData.postalCode || ''}
                        onChange={(e) => setDetailFormData({ ...detailFormData, postalCode: e.target.value })}
                        placeholder="Contoh: 46253"
                        maxLength={10}
                        className="w-full px-3.5 py-2 text-sm bg-slate-50 dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded-xl outline-none focus:ring-2 focus:ring-emerald-500 disabled:opacity-80 font-mono font-medium"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-slate-600 dark:text-slate-400 mb-1">
                      Status Peserta Didik
                    </label>
                    <select
                      disabled={!isEditingDetail}
                      value={detailFormData.status || 'Aktif'}
                      onChange={(e: any) => setDetailFormData({ ...detailFormData, status: e.target.value })}
                      className="w-full px-3.5 py-2 text-sm bg-slate-50 dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded-xl outline-none focus:ring-2 focus:ring-emerald-500 disabled:opacity-80"
                    >
                      <option value="Aktif">Aktif</option>
                      <option value="Tidak Aktif">Tidak Aktif</option>
                    </select>
                  </div>
                </div>
              )}

              {/* TAB 2: DATA ORANG TUA / WALI */}
              {detailTab === 'orangtua' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-bold text-slate-600 dark:text-slate-400 mb-1">
                      Nama Ayah Kandung
                    </label>
                    <input
                      type="text"
                      disabled={!isEditingDetail}
                      value={detailFormData.fatherName || ''}
                      onChange={(e) => setDetailFormData({ ...detailFormData, fatherName: e.target.value })}
                      placeholder="Nama ayah"
                      className="w-full px-3.5 py-2 text-sm bg-slate-50 dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded-xl outline-none focus:ring-2 focus:ring-emerald-500 disabled:opacity-80"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-slate-600 dark:text-slate-400 mb-1">
                      Nama Ibu Kandung
                    </label>
                    <input
                      type="text"
                      disabled={!isEditingDetail}
                      value={detailFormData.motherName || ''}
                      onChange={(e) => setDetailFormData({ ...detailFormData, motherName: e.target.value })}
                      placeholder="Nama ibu"
                      className="w-full px-3.5 py-2 text-sm bg-slate-50 dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded-xl outline-none focus:ring-2 focus:ring-emerald-500 disabled:opacity-80"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-slate-600 dark:text-slate-400 mb-1">
                      Nama Wali (Jika Ada)
                    </label>
                    <input
                      type="text"
                      disabled={!isEditingDetail}
                      value={detailFormData.guardianName || ''}
                      onChange={(e) => setDetailFormData({ ...detailFormData, guardianName: e.target.value })}
                      placeholder="Nama wali murid"
                      className="w-full px-3.5 py-2 text-sm bg-slate-50 dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded-xl outline-none focus:ring-2 focus:ring-emerald-500 disabled:opacity-80"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-slate-600 dark:text-slate-400 mb-1">
                      Nomor HP Orang Tua / Wali
                    </label>
                    <input
                      type="text"
                      disabled={!isEditingDetail}
                      value={detailFormData.parentPhone || ''}
                      onChange={(e) => setDetailFormData({ ...detailFormData, parentPhone: e.target.value })}
                      placeholder="08xxxxxxxxxx"
                      className="w-full px-3.5 py-2 text-sm bg-slate-50 dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded-xl outline-none focus:ring-2 focus:ring-emerald-500 disabled:opacity-80"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-slate-600 dark:text-slate-400 mb-1">
                      Pekerjaan Orang Tua / Wali
                    </label>
                    <input
                      type="text"
                      disabled={!isEditingDetail}
                      value={detailFormData.parentJob || ''}
                      onChange={(e) => setDetailFormData({ ...detailFormData, parentJob: e.target.value })}
                      placeholder="PNS / Wiraswasta / Petani / dll"
                      className="w-full px-3.5 py-2 text-sm bg-slate-50 dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded-xl outline-none focus:ring-2 focus:ring-emerald-500 disabled:opacity-80"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-slate-600 dark:text-slate-400 mb-1">
                      Kontak Darurat
                    </label>
                    <input
                      type="text"
                      disabled={!isEditingDetail}
                      value={detailFormData.emergencyContact || ''}
                      onChange={(e) => setDetailFormData({ ...detailFormData, emergencyContact: e.target.value })}
                      placeholder="Nama & No HP kontak darurat"
                      className="w-full px-3.5 py-2 text-sm bg-slate-50 dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded-xl outline-none focus:ring-2 focus:ring-emerald-500 disabled:opacity-80"
                    />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 md:col-span-2">
                    <div className="sm:col-span-2">
                      <label className="block text-xs font-bold text-slate-600 dark:text-slate-400 mb-1">
                        Alamat Orang Tua / Wali
                      </label>
                      <textarea
                        disabled={!isEditingDetail}
                        rows={2}
                        value={detailFormData.parentAddress ?? (detailFormData.address || '')}
                        onChange={(e) => setDetailFormData({ ...detailFormData, parentAddress: e.target.value })}
                        placeholder="Alamat domisili orang tua"
                        className="w-full px-3.5 py-2 text-sm bg-slate-50 dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded-xl outline-none focus:ring-2 focus:ring-emerald-500 disabled:opacity-80 resize-none"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-slate-600 dark:text-slate-400 mb-1 flex items-center gap-1">
                        <MapPin size={12} className="text-indigo-500" /> Kode Pos Ortu / Wali
                      </label>
                      <input
                        type="text"
                        disabled={!isEditingDetail}
                        value={detailFormData.parentPostalCode || ''}
                        onChange={(e) => setDetailFormData({ ...detailFormData, parentPostalCode: e.target.value })}
                        placeholder="Contoh: 46253"
                        maxLength={10}
                        className="w-full px-3.5 py-2 text-sm bg-slate-50 dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded-xl outline-none focus:ring-2 focus:ring-emerald-500 disabled:opacity-80 font-mono font-medium"
                      />
                    </div>
                  </div>
                </div>
              )}

               {/* TAB 3: DATA PENDUKUNG */}
              {detailTab === 'pendukung' && (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-bold text-slate-600 dark:text-slate-400 mb-1">
                        Status KIP / PIP
                      </label>
                      <select
                        disabled={!isEditingDetail}
                        value={detailFormData.kipPipStatus || 'Bukan Penerima'}
                        onChange={(e) => setDetailFormData({ ...detailFormData, kipPipStatus: e.target.value })}
                        className="w-full px-3.5 py-2 text-sm bg-slate-50 dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded-xl outline-none focus:ring-2 focus:ring-emerald-500 disabled:opacity-80 font-bold"
                      >
                        <option value="Bukan Penerima">Bukan Penerima</option>
                        <option value="Penerima KIP">Penerima KIP</option>
                        <option value="Penerima PIP">Penerima PIP</option>
                        <option value="Usulan KIP/PIP">Usulan KIP / PIP</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-xs font-bold text-slate-600 dark:text-slate-400 mb-1">
                        Rencana Studi Lanjutan
                      </label>
                      <select
                        disabled={!isEditingDetail}
                        value={detailFormData.rencanaStudiLanjutan || ''}
                        onChange={(e) => setDetailFormData({ ...detailFormData, rencanaStudiLanjutan: e.target.value })}
                        className="w-full px-3.5 py-2 text-sm bg-slate-50 dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded-xl outline-none focus:ring-2 focus:ring-emerald-500 disabled:opacity-80 font-bold"
                      >
                        <option value="">-- Pilih Rencana Lanjutan --</option>
                        <option value="SMA">SMA</option>
                        <option value="SMK">SMK</option>
                        <option value="MAN">MAN</option>
                        <option value="Pesantren">Pesantren</option>
                        <option value="Kerja">Kerja</option>
                      </select>
                    </div>

                    <div className="md:col-span-2">
                      <label className="block text-xs font-bold text-slate-600 dark:text-slate-400 mb-1">
                        Detail
                      </label>
                      <input
                        type="text"
                        disabled={!isEditingDetail}
                        value={detailFormData.rencanaStudiDetail || ''}
                        onChange={(e) => setDetailFormData({ ...detailFormData, rencanaStudiDetail: e.target.value })}
                        placeholder="Contoh: SMAN 1 Kawali, SMKN 1 Ciamis, kerja di pabrik, PKBM, pesantren al daurat"
                        className="w-full px-3.5 py-2 text-sm bg-slate-50 dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded-xl outline-none focus:ring-2 focus:ring-emerald-500 disabled:opacity-80 font-bold"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-slate-600 dark:text-slate-400 mb-1">
                      Foto Peserta Didik
                    </label>
                    {isEditingDetail ? (
                      <div className="flex flex-col sm:flex-row items-center gap-3 bg-slate-50 dark:bg-slate-900 p-3 rounded-xl border border-slate-200 dark:border-slate-700">
                        <input
                          type="text"
                          value={detailFormData.photoUrl || ''}
                          onChange={(e) => setDetailFormData({ ...detailFormData, photoUrl: e.target.value })}
                          placeholder="Masukkan URL Foto atau Upload File"
                          className="flex-1 px-3 py-1.5 text-xs bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-lg outline-none"
                        />
                        <label className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold rounded-lg cursor-pointer flex items-center gap-1 shrink-0">
                          <Camera size={14} /> Upload Foto
                          <input
                            type="file"
                            accept="image/*"
                            onChange={handlePhotoUpload}
                            className="hidden"
                          />
                        </label>
                      </div>
                    ) : (
                      <div className="flex items-center gap-3">
                        {detailFormData.photoUrl ? (
                          <img
                            src={detailFormData.photoUrl}
                            alt="Foto Siswa"
                            className="w-20 h-20 rounded-xl object-cover border border-slate-300"
                          />
                        ) : (
                          <div className="p-3 bg-slate-100 dark:bg-slate-900 rounded-xl text-xs text-slate-500 italic">
                            Belum ada foto terlampir.
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-slate-600 dark:text-slate-400 mb-1">
                      Dokumen Pendukung (Kartu Keluarga / Surat Keterangan / Lampiran)
                    </label>
                    {isEditingDetail ? (
                      <div className="flex flex-col sm:flex-row items-center gap-3 bg-slate-50 dark:bg-slate-900 p-3 rounded-xl border border-slate-200 dark:border-slate-700">
                        <input
                          type="text"
                          value={detailFormData.supportingDocUrl || ''}
                          onChange={(e) => setDetailFormData({ ...detailFormData, supportingDocUrl: e.target.value })}
                          placeholder="Link Google Drive atau Keterangan Dokumen"
                          className="flex-1 px-3 py-1.5 text-xs bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-lg outline-none"
                        />
                        <label className="px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold rounded-lg cursor-pointer flex items-center gap-1 shrink-0">
                          <FileBadge size={14} /> Upload Dokumen
                          <input
                            type="file"
                            onChange={handleDocUpload}
                            className="hidden"
                          />
                        </label>
                      </div>
                    ) : (
                      <div>
                        {detailFormData.supportingDocUrl ? (
                          <a
                            href={detailFormData.supportingDocUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1.5 text-xs font-bold text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-950/40 px-3 py-2 rounded-xl border border-emerald-200 dark:border-emerald-800"
                          >
                            <FileBadge size={16} /> Buka / Unduh Dokumen Pendukung
                          </a>
                        ) : (
                          <div className="p-3 bg-slate-100 dark:bg-slate-900 rounded-xl text-xs text-slate-500 italic">
                            Belum ada dokumen pendukung terlampir.
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-slate-600 dark:text-slate-400 mb-1">
                      Catatan Khusus Wali Kelas
                    </label>
                    <textarea
                      disabled={!isEditingDetail}
                      rows={3}
                      value={detailFormData.specialNotes || detailFormData.notes || ''}
                      onChange={(e) =>
                        setDetailFormData({
                          ...detailFormData,
                          specialNotes: e.target.value,
                          notes: e.target.value,
                        })
                      }
                      placeholder="Catatan riwayat kesehatan, kepribadian, bantuan sosial, atau perhatian khusus wali kelas..."
                      className="w-full px-3.5 py-2 text-sm bg-slate-50 dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded-xl outline-none focus:ring-2 focus:ring-emerald-500 disabled:opacity-80"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="p-4 bg-slate-100 dark:bg-slate-900/80 border-t border-slate-200 dark:border-slate-700 flex items-center justify-between shrink-0">
              <button
                onClick={() => setSelectedStudentDetail(null)}
                className="px-4 py-2 text-xs font-bold text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-800 rounded-xl"
              >
                Tutup
              </button>

              <div className="flex items-center gap-2">
                {!isEditingDetail ? (
                  <button
                    onClick={() => setIsEditingDetail(true)}
                    className="px-5 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold rounded-xl shadow flex items-center gap-1.5"
                  >
                    <Edit3 size={14} /> Edit Data Siswa
                  </button>
                ) : (
                  <button
                    onClick={handleSaveStudentDetail}
                    className="px-5 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold rounded-xl shadow flex items-center gap-1.5"
                  >
                    <Check size={14} /> Simpan Database Siswa
                  </button>
                )}
              </div>
            </div>

          </div>
        </div>
      )}
    </div>
  );
};

export default PerwalianView;
